import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { VariantPicker } from "@/components/variant-picker";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Upload, X, Pencil, ChevronLeft, ChevronRight, Plus, Target, Brain, Layers, FileText, Crosshair, Sparkles, Loader2, Download, Users, Package, Tag, BookOpen, Trash2, FileJson, Eye, Zap, MessageSquare, Palette, Shield, BarChart3, AlertTriangle, CheckCircle2, Lightbulb, Hash, Check, Paintbrush, ScrollText, Film, Clapperboard, FlaskConical, BookmarkPlus, BookmarkCheck, Clock } from "lucide-react";
import { InpaintingModal } from "@/components/campaign-wizard/inpainting-modal";
import { AdImage } from "@/components/ad-image";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { groupAnglesByFamily } from "@/lib/angle-groups";
import { usePickerSearch, PickerSearchHeader, PickerNoMatches } from "@/lib/picker-search";
import { Link } from "wouter";
import { useState, useRef, useCallback, useEffect } from "react";
import type { AdUnitType, Persona, Product, Offer, NarrativeAngle, Brand } from "@shared/schema";
import { type AdImageRecord, displayUrl, normalizeExampleImage, hasUsableExampleImage } from "@shared/adImage";
import { apiRequest, getQueryFn, invalidateGeneratedAdListings, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { type AspectRatio, AD_ASPECT_RATIO_OPTIONS, AD_ASPECT_RATIOS } from "@shared/aspectRatios";
import { loadLastAspectRatio, saveLastAspectRatio, loadLastAspectRatios, saveLastAspectRatios } from "@/lib/aspectRatio";
import { planInstantAdMultiRatio } from "@/lib/instant-ad-multi-ratio";
import { AdUnitBrandCuration } from "@/components/ad-unit-brand-curation";

// Render narrative-angle <SelectItem>s grouped under Opportunity Group headers.
function renderGroupedAngleItems(angles: NarrativeAngle[]) {
  return groupAnglesByFamily(angles).map((g) => (
    <SelectGroup key={g.label}>
      <SelectLabel className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground/70">
        {g.label}
      </SelectLabel>
      {g.angles.map((a) => (
        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
      ))}
    </SelectGroup>
  ));
}

// Plain Select with in-dropdown search (4+ item threshold), matching the
// PickerSelect search behavior used on Ads Express / LP Express / Creative Tinder.
function SearchableSelect({
  value, onValueChange, disabled, placeholder, testId,
  options, angles,
}: {
  value: string;
  onValueChange: (v: string) => void;
  disabled?: boolean;
  placeholder: string;
  testId: string;
  options?: { value: string; label: string }[];
  angles?: NarrativeAngle[];
}) {
  const [open, setOpen] = useState(false);
  const count = angles ? angles.length : (options?.length ?? 0);
  const { showSearch, query, setQuery, searchInputRef, isSearching, matchesLabel } =
    usePickerSearch(open, count, true);
  const filteredOptions = isSearching ? (options ?? []).filter((o) => matchesLabel(o.label)) : (options ?? []);
  const filteredAngles = isSearching ? (angles ?? []).filter((a) => matchesLabel(a.name)) : (angles ?? []);
  const hasAnyResult = angles ? filteredAngles.length > 0 : filteredOptions.length > 0;
  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled} open={open} onOpenChange={setOpen}>
      <SelectTrigger className="h-9 text-sm" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {showSearch && (
          <PickerSearchHeader
            inputRef={searchInputRef}
            query={query}
            onQueryChange={setQuery}
            testId={`input-search-${testId}`}
          />
        )}
        {showSearch && !hasAnyResult && <PickerNoMatches />}
        {angles
          ? renderGroupedAngleItems(filteredAngles)
          : filteredOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function toImageRecords(raw: unknown): AdImageRecord[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => normalizeExampleImage(entry)).filter((r): r is AdImageRecord => r != null);
}

const FUNNEL_STAGES = ["Awareness", "Knowledge", "Liking", "Preference", "Conviction", "Purchase"] as const;
const FUNNEL_COLORS: Record<string, string> = {
  Awareness: "bg-violet-500", Knowledge: "bg-blue-500", Liking: "bg-sky-500",
  Preference: "bg-indigo-500", Conviction: "bg-amber-500", Purchase: "bg-emerald-500",
};

function useInlineSave(adUnitId: string, brandId: string) {
  const { toast } = useToast();
  const mutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const res = await apiRequest("PATCH", `/api/ad-units/${adUnitId}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ad-units", adUnitId] });
      queryClient.invalidateQueries({ queryKey: ["/api/ad-units"] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-units`] });
    },
    onError: (e: Error) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });
  return mutation;
}

function InlineText({ value, field, onSave, placeholder, className, mono }: {
  value: string | null | undefined;
  field: string;
  onSave: (patch: Record<string, any>) => void;
  placeholder?: string;
  className?: string;
  mono?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = () => {
    setEditing(false);
    if (draft !== (value || "")) onSave({ [field]: draft || null });
  };

  if (editing) {
    return (
      <Input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(value || ""); setEditing(false); } }}
        className={`${mono ? "font-mono text-xs" : "text-sm"} ${className || ""}`}
        placeholder={placeholder}
        data-testid={`inline-edit-${field}`}
      />
    );
  }

  return (
    <div
      onClick={() => { setDraft(value || ""); setEditing(true); }}
      className={`group cursor-pointer rounded-md px-2 py-1 -mx-2 hover:bg-muted/40 transition-colors min-h-[28px] flex items-center gap-2 ${className || ""}`}
      data-testid={`inline-value-${field}`}
    >
      <span className={`${mono ? "font-mono text-xs" : "text-sm"} ${value ? "text-foreground/90" : "text-muted-foreground/50 italic"} leading-relaxed`}>
        {value || placeholder || "Click to add..."}
      </span>
      <Pencil className="h-3 w-3 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </div>
  );
}

function InlineArea({ value, field, onSave, placeholder, mono, rows }: {
  value: string | null | undefined;
  field: string;
  onSave: (patch: Record<string, any>) => void;
  placeholder?: string;
  mono?: boolean;
  rows?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const draftRef = useRef(draft);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { draftRef.current = draft; }, [draft]);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const save = () => {
    const currentDraft = draftRef.current;
    setEditing(false);
    if (currentDraft !== (value || "")) onSave({ [field]: currentDraft || null });
  };

  if (editing) {
    return (
      <div className="space-y-1.5">
        <Textarea
          ref={ref}
          value={draft}
          onChange={e => { setDraft(e.target.value); draftRef.current = e.target.value; }}
          onBlur={save}
          onKeyDown={e => { if (e.key === "Escape") { setDraft(value || ""); draftRef.current = value || ""; setEditing(false); } }}
          rows={rows || 4}
          className={`${mono ? "font-mono text-xs" : "text-sm"} resize-y`}
          placeholder={placeholder}
          data-testid={`inline-edit-${field}`}
        />
      </div>
    );
  }

  return (
    <div
      onClick={() => { setDraft(value || ""); setEditing(true); }}
      className="group cursor-pointer rounded-md px-2 py-1.5 -mx-2 hover:bg-muted/40 transition-colors min-h-[36px]"
      data-testid={`inline-value-${field}`}
    >
      <div className="flex items-start gap-2">
        <span className={`${mono ? "font-mono text-xs whitespace-pre-wrap" : "text-sm"} ${value ? "text-foreground/90" : "text-muted-foreground/50 italic"} leading-relaxed flex-1`}>
          {value || placeholder || "Click to add..."}
        </span>
        <Pencil className="h-3 w-3 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
      </div>
    </div>
  );
}

function InlineNumber({ value, field, onSave, placeholder, suffix }: {
  value: number | null | undefined;
  field: string;
  onSave: (patch: Record<string, any>) => void;
  placeholder?: string;
  suffix?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value?.toString() || "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = () => {
    setEditing(false);
    const num = draft ? parseInt(draft, 10) : null;
    if (num !== value) onSave({ [field]: num });
  };

  if (editing) {
    return (
      <Input
        ref={inputRef}
        type="number"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(value?.toString() || ""); setEditing(false); } }}
        className="text-sm w-24"
        placeholder={placeholder}
        data-testid={`inline-edit-${field}`}
      />
    );
  }

  return (
    <div
      onClick={() => { setDraft(value?.toString() || ""); setEditing(true); }}
      className="group cursor-pointer rounded-md px-2 py-1 -mx-2 hover:bg-muted/40 transition-colors min-h-[28px] flex items-center gap-2"
      data-testid={`inline-value-${field}`}
    >
      <span className={`text-sm font-medium ${value != null ? "text-foreground/90" : "text-muted-foreground/50 italic"}`}>
        {value != null ? `${value}${suffix || ""}` : (placeholder || "Click to add...")}
      </span>
      <Pencil className="h-3 w-3 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </div>
  );
}

function InlineJson({ value, field, onSave, placeholder }: {
  value: unknown;
  field: string;
  onSave: (patch: Record<string, any>) => void;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const raw = value != null ? JSON.stringify(value, null, 2) : "";
  const [draft, setDraft] = useState(raw);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const save = () => {
    setEditing(false);
    if (draft === raw) return;
    try {
      const parsed = draft.trim() ? JSON.parse(draft) : null;
      onSave({ [field]: parsed });
    } catch {
      onSave({ [field]: draft || null });
    }
  };

  if (editing) {
    return (
      <Textarea
        ref={ref}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === "Escape") { setDraft(raw); setEditing(false); } }}
        rows={Math.max(4, (draft || "").split("\n").length + 1)}
        className="font-mono text-xs resize-y"
        placeholder={placeholder || "Enter JSON..."}
        data-testid={`inline-edit-${field}`}
      />
    );
  }

  if (!value) {
    return (
      <div
        onClick={() => { setDraft(""); setEditing(true); }}
        className="group cursor-pointer rounded-md px-2 py-1.5 -mx-2 hover:bg-muted/40 transition-colors"
        data-testid={`inline-value-${field}`}
      >
        <span className="text-sm text-muted-foreground/50 italic flex items-center gap-2">
          {placeholder || "Click to add..."}
          <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
        </span>
      </div>
    );
  }

  return (
    <div
      onClick={() => { setDraft(raw); setEditing(true); }}
      className="group cursor-pointer rounded-md px-2 py-1.5 -mx-2 hover:bg-muted/40 transition-colors"
      data-testid={`inline-value-${field}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <JsonOrListDisplay data={value as any} />
        </div>
        <Pencil className="h-3 w-3 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
      </div>
    </div>
  );
}

function InlineSelect({ value, field, options, onSave, placeholder }: {
  value: string | null | undefined;
  field: string;
  options: string[];
  onSave: (patch: Record<string, any>) => void;
  placeholder?: string;
}) {
  return (
    <Select value={value || ""} onValueChange={v => onSave({ [field]: v })}>
      <SelectTrigger className="h-8 text-sm w-auto min-w-[140px] border-transparent hover:border-border/40 transition-colors" data-testid={`inline-select-${field}`}>
        <SelectValue placeholder={placeholder || "Select..."} />
      </SelectTrigger>
      <SelectContent>
        {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function AdUnitDetailPage() {
  const { brandId, adUnitId } = useParams<{ brandId: string; adUnitId: string }>();
  const { user } = useAuth();
  const isAdmin = !!user?.isMaster || !!user?.isSuperuser;
  const { toast } = useToast();
  const [importOpen, setImportOpen] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: adUnit, isLoading } = useQuery<AdUnitType>({
    queryKey: ["/api/ad-units", adUnitId],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!adUnitId,
  });

  const saveMutation = useInlineSave(adUnitId, brandId);
  const save = isAdmin ? (patch: Record<string, any>) => saveMutation.mutate(patch) : () => {};

  const imageRecords: AdImageRecord[] = toImageRecords(adUnit?.exampleImages);
  const images: string[] = imageRecords
    .map((r) => displayUrl(r))
    .filter((u): u is string => !!u);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (previewIndex === null) return;
    if (e.key === "ArrowLeft" && previewIndex > 0) setPreviewIndex(p => p !== null ? p - 1 : null);
    else if (e.key === "ArrowRight" && previewIndex < images.length - 1) setPreviewIndex(p => p !== null ? p + 1 : null);
    else if (e.key === "Escape") setPreviewIndex(null);
  }, [previewIndex, images.length]);

  useEffect(() => {
    if (previewIndex !== null) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [previewIndex, handleKeyDown]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !adUnit) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await fetch("/api/upload", { method: "POST", body: formData });
      if (!resp.ok) throw new Error("Upload failed");
      const uploadData = await resp.json();
      const url = uploadData.falUrl || uploadData.url;
      // Prefer the durable R2 copy already produced by /api/upload so the
      // server can reuse it instead of re-downloading + re-uploading.
      const newEntry: { imageUrlOriginal: string; imageUrlDurable?: string } = {
        imageUrlOriginal: url,
      };
      if (uploadData.r2Url) newEntry.imageUrlDurable = uploadData.r2Url;
      const currentImages = toImageRecords(adUnit.exampleImages);
      await apiRequest("PATCH", `/api/ad-units/${adUnitId}`, {
        exampleImages: [...currentImages, newEntry],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/ad-units", adUnitId] });
      queryClient.invalidateQueries({ queryKey: ["/api/ad-units"] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-units`] });
      toast({ title: "Image uploaded" });
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = async (idx: number) => {
    if (!adUnit) return;
    try {
      const currentImages = toImageRecords(adUnit.exampleImages);
      const next = currentImages.filter((_, i) => i !== idx);
      await apiRequest("PATCH", `/api/ad-units/${adUnitId}`, { exampleImages: next });
      queryClient.invalidateQueries({ queryKey: ["/api/ad-units", adUnitId] });
      queryClient.invalidateQueries({ queryKey: ["/api/ad-units"] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-units`] });
      toast({ title: "Image removed" });
    } catch (err: any) {
      toast({ title: "Failed to remove image", description: err.message, variant: "destructive" });
    }
  };

  const handleImportJson = async () => {
    if (!importJson.trim()) return;
    setImporting(true);
    try {
      const data = JSON.parse(importJson);
      const patch: Record<string, unknown> = {};
      if (data.ad_unit_name) patch.name = data.ad_unit_name;
      if (data.visual_identification) patch.visualIdentification = data.visual_identification;
      if (data.strategic_role?.description) patch.strategicRole = data.strategic_role.description;
      if (data.primary_funnel) {
        const raw = data.primary_funnel.toLowerCase();
        const normalized = FUNNEL_STAGES.find(s => raw.includes(s.toLowerCase()))
          || (raw.includes("top") ? "Awareness" : null)
          || (raw.includes("mid") ? "Liking" : null)
          || (raw.includes("bottom") ? "Purchase" : null)
          || data.primary_funnel;
        patch.funnelStage = normalized;
      }
      if (data.secondary) patch.secondaryFunnel = data.secondary;
      if (data.objective) patch.objective = data.objective;
      if (data.psychological_function) patch.psychologicalFunction = data.psychological_function;
      if (data.structural_drivers) patch.structuralDrivers = data.structural_drivers;
      if (data.scroll_stopper) patch.scrollStopper = data.scroll_stopper;
      if (data.core_message) patch.coreMessage = data.core_message;
      if (data.psychological_levers) patch.psychologicalLevers = data.psychological_levers;
      if (data.creative_elements) patch.creativeElements = data.creative_elements;
      if (data.reusability_score !== undefined) patch.reusabilityScore = data.reusability_score;
      if (data.copy_framework) patch.copyFramework = data.copy_framework;
      if (data.hook_style) patch.hookStyle = data.hook_style;
      if (data.proof_patterns) patch.proofPatterns = data.proof_patterns;
      if (data.cta_patterns) patch.ctaPatterns = data.cta_patterns;
      if (data.visual_grammar) patch.visualGrammar = data.visual_grammar;
      if (data.use_when) patch.useWhen = data.use_when;
      if (data.avoid_when) patch.avoidWhen = data.avoid_when;
      if (data.notes) patch.notes = data.notes;
      if (data.image_prompt_template) patch.imagePromptTemplate = data.image_prompt_template;
      if (data.starting_frame_prompt) patch.startingFramePrompt = data.starting_frame_prompt;
      if (data.format_type) patch.formatType = data.format_type;
      if (data.platform) patch.platform = data.platform;
      if (data.structure_template) patch.structureTemplate = data.structure_template;
      if (data.funnel_deployment) patch.funnelDeployment = data.funnel_deployment;

      if (Object.keys(patch).length === 0) {
        toast({ title: "No recognized fields", description: "The JSON didn't contain any fields that map to ad unit metadata.", variant: "destructive" });
        return;
      }
      await apiRequest("PATCH", `/api/ad-units/${adUnitId}`, patch);
      queryClient.invalidateQueries({ queryKey: ["/api/ad-units", adUnitId] });
      queryClient.invalidateQueries({ queryKey: ["/api/ad-units"] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-units`] });
      toast({ title: "Import successful", description: `Updated ${Object.keys(patch).length} field(s)` });
      setImportOpen(false);
      setImportJson("");
    } catch (e: any) {
      toast({ title: "Import failed", description: e.message, variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 max-w-6xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-80 rounded-2xl" />
        <div className="grid grid-cols-2 gap-4"><Skeleton className="h-40" /><Skeleton className="h-40" /></div>
      </div>
    );
  }
  if (!adUnit) return <div className="p-8">Ad unit not found</div>;

  const secondaryFunnelRaw = (adUnit as any).secondaryFunnel;
  const secondaryFunnel = typeof secondaryFunnelRaw === "string" ? secondaryFunnelRaw : null;

  // Mirror the picker/server eligibility check. A unit with no usable
  // example (or meme reference) image hard-fails at render time and is
  // hidden from the Ads Express picker for non-admins, so warn the editor.
  const isMemeUnit = (adUnit as any).isMeme === true;
  const hasUsableExample = hasUsableExampleImage(adUnit as any);

  return (
    <div className="max-w-6xl mx-auto">
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="input-upload-image" />

      <div className="px-8 pt-6 pb-2 flex items-center justify-between gap-4 fade-in-up">
        <div className="flex items-center gap-3">
          <Link href={`/brands/${brandId}/ad-units`}>
            <Button variant="ghost" size="icon" className="rounded-md" data-testid="button-back-ad-units"><ArrowLeft className="h-4 w-4" /></Button>
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <InlineText value={adUnit.name} field="name" onSave={save} className="text-2xl font-bold tracking-tight" placeholder="Ad unit name..." />
            <Badge variant={adUnit.status === "active" ? "default" : "secondary"} className="text-[10px] rounded-full px-2.5">{adUnit.status}</Badge>
            <InlineSelect value={adUnit.formatType} field="formatType" options={["static", "video", "carousel", "ugc", "meme", "pixar", "other"]} onSave={save} placeholder="Format..." />
            {adUnit.formatIndex != null && (
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">#{String(adUnit.formatIndex).padStart(2, "0")}</span>
            )}
          </div>
        </div>
        {isAdmin && adUnit.formatType !== "static" && (
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setImportJson(""); setImportOpen(true); }} data-testid="button-import-json">
            <FileJson className="h-3.5 w-3.5 mr-1.5" /> Import JSON
          </Button>
        )}
      </div>

      {!hasUsableExample && (
        <div className="px-8 pt-1 pb-3 fade-in-up">
          <div
            className="flex items-start gap-3 rounded-xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-200"
            data-testid="banner-no-example-image"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="text-sm leading-snug">
              <span className="font-semibold">
                No usable {isMemeUnit ? "meme reference image" : "example image"}.
              </span>{" "}
              This template can't be generated and is hidden from the Ads Express picker for
              regular users until you add {isMemeUnit ? "a meme reference image" : "an example image"}
              {isMemeUnit ? " in the meme settings below." : " below."}
            </div>
          </div>
        </div>
      )}

      <div className="px-8 pb-4 fade-in-up">
        <div className="flex flex-wrap items-center gap-3">
          {images.map((img, i) => (
            <div key={i} className="relative group rounded-2xl overflow-hidden border border-border/40 bg-muted/10 w-[160px] shrink-0">
              <button onClick={() => setPreviewIndex(i)} className="block w-full cursor-pointer focus:outline-none" data-testid={`button-preview-image-${i}`}>
                <AdImage src={img} alt={`Example ${i + 1}`} className="w-full aspect-[3/4] object-cover" data-testid={`img-example-${i}`} />
              </button>
              {isAdmin && (
                <button onClick={() => handleRemoveImage(i)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-background/80 backdrop-blur flex items-center justify-center invisible group-hover:visible transition-opacity hover:bg-destructive hover:text-destructive-foreground" data-testid={`button-remove-image-${i}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          {isAdmin && (
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-[160px] aspect-[3/4] shrink-0 rounded-2xl border-2 border-dashed border-border/40 hover:border-primary/40 bg-muted/5 flex flex-col items-center justify-center text-muted-foreground hover:text-primary transition-colors cursor-pointer" data-testid={images.length > 0 ? "button-upload-image" : "button-upload-first-image"}>
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                {uploading ? <div className="h-4 w-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /> : <Plus className="h-5 w-5 text-primary/60" />}
              </div>
              <span className="text-xs font-medium">{uploading ? "Uploading..." : "Add Image"}</span>
            </button>
          )}
        </div>
      </div>

      <div className="px-8 pb-8 space-y-5">
        {isAdmin && adUnit.formatType !== "ugc" && adUnit.formatType !== "pixar" && (
          <AdUnitBrandCuration adUnit={adUnit} currentBrandId={brandId} />
        )}

        {adUnit.formatType !== "static" && (
          <div className="contents" data-testid="section-legacy-ad-unit-metadata">
            <FieldSection icon={<Target className="h-4 w-4 text-primary" />} label="Strategic Role" accent>
              <InlineArea value={adUnit.strategicRole} field="strategicRole" onSave={save} placeholder="Describe the strategic role of this ad unit..." rows={3} />
            </FieldSection>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 fade-in-up-delay-1">
              {FUNNEL_STAGES.map((stage, i) => {
                const isActive = adUnit.funnelStage === stage;
                const isSecondary = secondaryFunnel?.toLowerCase() === stage.toLowerCase();
                return (
                  <button
                    key={stage}
                    onClick={() => save({ funnelStage: stage })}
                    className={`relative rounded-xl px-3 py-2.5 text-center transition-all border cursor-pointer hover:border-primary/40 ${
                      isActive ? "border-primary/40 bg-primary/10 shadow-sm"
                      : isSecondary ? "border-primary/20 bg-primary/5"
                      : "border-border/30 bg-muted/5 opacity-40 hover:opacity-70"
                    }`}
                    data-testid={`funnel-stage-${stage.toLowerCase()}`}
                  >
                    <div className={`w-6 h-6 rounded-full mx-auto mb-1.5 flex items-center justify-center text-white text-[10px] font-bold ${FUNNEL_COLORS[stage] || "bg-gray-400"} ${!isActive && !isSecondary ? "opacity-40" : ""}`}>
                      {i + 1}
                    </div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider">{stage}</p>
                    {isActive && <p className="text-[9px] text-primary font-medium mt-0.5">Primary</p>}
                    {isSecondary && !isActive && <p className="text-[9px] text-muted-foreground font-medium mt-0.5">Secondary</p>}
                  </button>
                );
              })}
            </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 fade-in-up-delay-1">
          <FieldCard icon={<Crosshair className="h-3.5 w-3.5" />} label="Funnel Stage">
            <InlineSelect value={adUnit.funnelStage} field="funnelStage" options={[...FUNNEL_STAGES]} onSave={save} placeholder="Select..." />
          </FieldCard>
          <FieldCard icon={<Layers className="h-3.5 w-3.5" />} label="Secondary Funnel">
            <InlineSelect value={secondaryFunnel} field="secondaryFunnel" options={[...FUNNEL_STAGES]} onSave={save} placeholder="Select..." />
          </FieldCard>
          <FieldCard icon={<Target className="h-3.5 w-3.5" />} label="Objective">
            <InlineText value={adUnit.objective} field="objective" onSave={save} placeholder="Objective..." />
          </FieldCard>
          <FieldCard icon={<Hash className="h-3.5 w-3.5" />} label="Ideal Length (s)">
            <InlineNumber value={adUnit.idealLengthSeconds} field="idealLengthSeconds" onSave={save} placeholder="Seconds" suffix="s" />
          </FieldCard>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 fade-in-up-delay-1">
          <FieldCard icon={<BarChart3 className="h-3.5 w-3.5" />} label="Reusability Score">
            <InlineNumber value={(adUnit as any).reusabilityScore} field="reusabilityScore" onSave={save} placeholder="1-10" suffix="/10" />
          </FieldCard>
          <FieldCard icon={<Hash className="h-3.5 w-3.5" />} label="Platform">
            <InlineJson value={adUnit.platform} field="platform" onSave={save} placeholder="Platform(s)..." />
          </FieldCard>
        </div>

        <FieldSection icon={<Zap className="h-4 w-4 text-amber-500" />} label="Scroll Stopper" borderColor="border-l-amber-500/60">
          <InlineArea value={(adUnit as any).scrollStopper} field="scrollStopper" onSave={save} placeholder="What stops the scroll?" rows={2} />
        </FieldSection>

        <FieldSection icon={<MessageSquare className="h-4 w-4 text-primary" />} label="Core Message" borderColor="border-l-primary/40">
          <InlineArea value={(adUnit as any).coreMessage} field="coreMessage" onSave={save} placeholder="The core message of this ad unit..." rows={2} />
        </FieldSection>

        <FieldSection icon={<Eye className="h-4 w-4" />} label="Visual Identification">
          <InlineJson value={(adUnit as any).visualIdentification} field="visualIdentification" onSave={save} placeholder="Add visual identification..." />
        </FieldSection>

        <FieldSection icon={<Brain className="h-4 w-4" />} label="Psychological Function">
          <InlineJson value={adUnit.psychologicalFunction} field="psychologicalFunction" onSave={save} placeholder="Add psychological function..." />
        </FieldSection>

        <FieldSection icon={<Brain className="h-4 w-4" />} label="Psychological Levers">
          <InlineJson value={(adUnit as any).psychologicalLevers} field="psychologicalLevers" onSave={save} placeholder="Add psychological levers..." />
        </FieldSection>

        <FieldSection icon={<Palette className="h-4 w-4" />} label="Creative Elements">
          <InlineJson value={(adUnit as any).creativeElements} field="creativeElements" onSave={save} placeholder="Add creative elements..." />
        </FieldSection>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 fade-in-up-delay-2">
          <FieldSection icon={<Layers className="h-4 w-4" />} label="Structural Drivers">
            <InlineJson value={adUnit.structuralDrivers} field="structuralDrivers" onSave={save} placeholder="Add structural drivers..." />
          </FieldSection>
          <FieldSection icon={<FileText className="h-4 w-4" />} label="Copy Framework">
            <InlineJson value={adUnit.copyFramework} field="copyFramework" onSave={save} placeholder="Add copy framework..." />
          </FieldSection>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 fade-in-up-delay-2">
          <FieldSection icon={<Zap className="h-4 w-4" />} label="Hook Style">
            <InlineJson value={adUnit.hookStyle} field="hookStyle" onSave={save} placeholder="Add hook style..." />
          </FieldSection>
          <FieldSection icon={<Shield className="h-4 w-4" />} label="Proof Patterns">
            <InlineJson value={adUnit.proofPatterns} field="proofPatterns" onSave={save} placeholder="Add proof patterns..." />
          </FieldSection>
          <FieldSection icon={<Target className="h-4 w-4" />} label="CTA Patterns">
            <InlineJson value={adUnit.ctaPatterns} field="ctaPatterns" onSave={save} placeholder="Add CTA patterns..." />
          </FieldSection>
          <FieldSection icon={<Palette className="h-4 w-4" />} label="Visual Grammar">
            <InlineJson value={adUnit.visualGrammar} field="visualGrammar" onSave={save} placeholder="Add visual grammar..." />
          </FieldSection>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 fade-in-up-delay-2">
          <FieldSection icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />} label="Use When" borderColor="border-l-emerald-500/40">
            <InlineJson value={adUnit.useWhen} field="useWhen" onSave={save} placeholder="When to use this ad unit..." />
          </FieldSection>
          <FieldSection icon={<AlertTriangle className="h-4 w-4 text-red-400" />} label="Avoid When" borderColor="border-l-red-500/40">
            <InlineJson value={adUnit.avoidWhen} field="avoidWhen" onSave={save} placeholder="When to avoid this ad unit..." />
          </FieldSection>
        </div>

            <FieldSection icon={<Lightbulb className="h-4 w-4" />} label="Notes">
              <InlineJson value={adUnit.notes} field="notes" onSave={save} placeholder="Add notes..." />
            </FieldSection>
          </div>
        )}

        {(adUnit.formatType !== "ugc" || isAdmin) && (
          <>
            <FieldSection icon={<Crosshair className="h-4 w-4" />} label="Custom Template Prompt">
              <InlineArea value={adUnit.imagePromptTemplate} field="imagePromptTemplate" onSave={save} placeholder="Enter your prompt template for generating static images..." mono rows={8} />
            </FieldSection>

            {adUnit.formatType === "pixar" && (
              <FieldSection icon={<Film className="h-4 w-4" />} label="Storyboard Prompt Template">
                <InlineArea value={(adUnit as any).storyboardPromptTemplate} field="storyboardPromptTemplate" onSave={save} placeholder="Enter the storyboard prompt template for this Pixar ad unit..." mono rows={8} />
              </FieldSection>
            )}

            {adUnit.formatType === "pixar" && (
              <FieldSection icon={<Clapperboard className="h-4 w-4" />} label="SeeDance 2.0 Prompt Template">
                <InlineArea value={(adUnit as any).seedancePromptTemplate} field="seedancePromptTemplate" onSave={save} placeholder="Enter the SeeDance 2.0 prompt template for this Pixar ad unit..." mono rows={8} />
              </FieldSection>
            )}

            {adUnit.formatType === "pixar" && (
              <div className="bg-violet-50/50 dark:bg-violet-950/20 border border-violet-200/60 dark:border-violet-800/40 rounded-2xl p-5 space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="h-4 w-4 text-violet-500" />
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400" data-testid="section-pixar-sim-endpoints">Sim Studio Endpoints</h3>
                </div>
                <FieldSection icon={<Sparkles className="h-4 w-4" />} label="Creative Strategist Endpoint">
                  <InlineText value={(adUnit as any).simCreativeStrategistUrl} field="simCreativeStrategistUrl" onSave={save} placeholder="https://www.sim.ai/api/workflows/.../execute" mono />
                </FieldSection>
                <FieldSection icon={<Film className="h-4 w-4" />} label="Storyboard Maker Prompt Endpoint">
                  <InlineText value={(adUnit as any).simStoryboardMakerUrl} field="simStoryboardMakerUrl" onSave={save} placeholder="https://www.sim.ai/api/workflows/.../execute" mono />
                </FieldSection>
                <FieldSection icon={<Film className="h-4 w-4" />} label="Mini Storyboard Maker Prompt Endpoint">
                  <InlineText value={(adUnit as any).simMiniStoryboardMakerUrl} field="simMiniStoryboardMakerUrl" onSave={save} placeholder="https://www.sim.ai/api/workflows/.../execute" mono />
                </FieldSection>
                <FieldSection icon={<Sparkles className="h-4 w-4" />} label="Custom Concept Endpoint">
                  <InlineText value={(adUnit as any).simCustomConceptUrl} field="simCustomConceptUrl" onSave={save} placeholder="https://www.sim.ai/api/workflows/.../execute" mono />
                </FieldSection>
                <FieldSection icon={<Clapperboard className="h-4 w-4" />} label="SeeDance 2.0 Video Generation Prompt Endpoint">
                  <InlineText value={(adUnit as any).simSeedanceVideoUrl} field="simSeedanceVideoUrl" onSave={save} placeholder="https://www.sim.ai/api/workflows/.../execute" mono />
                </FieldSection>
              </div>
            )}

            {adUnit.formatType === "ugc" && (
              <FieldSection icon={<Users className="h-4 w-4" />} label="Starting Frame Prompt">
                <InlineArea value={adUnit.startingFramePrompt} field="startingFramePrompt" onSave={save} placeholder="Enter the starting frame prompt for this UGC ad unit..." mono rows={6} />
              </FieldSection>
            )}

            {adUnit.formatType === "ugc" && (
              <div className="bg-violet-50/50 dark:bg-violet-950/20 border border-violet-200/60 dark:border-violet-800/40 rounded-2xl p-5 space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="h-4 w-4 text-violet-500" />
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400" data-testid="section-sim-endpoints">Sim Studio Endpoints</h3>
                </div>
                <FieldSection icon={<Sparkles className="h-4 w-4" />} label="Starting Frame Prompt Endpoint">
                  <InlineText value={adUnit.simStartingFrameUrl} field="simStartingFrameUrl" onSave={save} placeholder="https://www.sim.ai/api/workflows/.../execute" mono />
                </FieldSection>
                <FieldSection icon={<Sparkles className="h-4 w-4" />} label="Script Concept Planner Endpoint">
                  <InlineText value={adUnit.simScriptConceptUrl} field="simScriptConceptUrl" onSave={save} placeholder="https://www.sim.ai/api/workflows/.../execute" mono />
                </FieldSection>
                <FieldSection icon={<ScrollText className="h-4 w-4" />} label="Script Generation Endpoint">
                  <InlineText value={adUnit.simScriptUrl} field="simScriptUrl" onSave={save} placeholder="https://www.sim.ai/api/workflows/.../execute" mono />
                </FieldSection>
                <FieldSection icon={<Film className="h-4 w-4" />} label="Scene Prompt Endpoint">
                  <InlineText value={adUnit.simScenePromptUrl} field="simScenePromptUrl" onSave={save} placeholder="https://www.sim.ai/api/workflows/.../execute" mono />
                </FieldSection>
              </div>
            )}
          </>
        )}

        <div className="fade-in-up-delay-2">
          {adUnit.formatType === "ugc"
            ? <InstantUGCSection brandId={brandId} adUnit={adUnit} />
            : <InstantAdGenSection brandId={brandId} adUnit={adUnit} />
          }
        </div>
      </div>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="tracking-tight">Import JSON</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Paste ad unit JSON to update this unit's metadata. Recognized fields will be mapped automatically.</p>
            <Textarea
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
              rows={16}
              placeholder='{"ad_unit_name": "...", "strategic_role": {...}, ...}'
              className="font-mono text-xs resize-y"
              data-testid="textarea-import-json"
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setImportOpen(false)} className="rounded-full" data-testid="button-cancel-import">Cancel</Button>
            <Button onClick={handleImportJson} disabled={importing || !importJson.trim()} className="rounded-full" data-testid="button-confirm-import">
              {importing ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={previewIndex !== null} onOpenChange={(open) => { if (!open) setPreviewIndex(null); }}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[95vh] p-0 overflow-hidden bg-black/95 border-border/30" aria-describedby={undefined} data-testid="dialog-image-preview">
          <DialogTitle className="sr-only">Image Preview</DialogTitle>
          <div className="relative flex items-center justify-center min-h-[60vh]">
            <button onClick={() => setPreviewIndex(null)} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="button-close-preview">
              <X className="h-4 w-4 text-white" />
            </button>
            {images.length > 1 && previewIndex !== null && previewIndex > 0 && (
              <button onClick={() => setPreviewIndex(p => p !== null ? p - 1 : null)} className="absolute left-3 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="button-preview-prev">
                <ChevronLeft className="h-5 w-5 text-white" />
              </button>
            )}
            {images.length > 1 && previewIndex !== null && previewIndex < images.length - 1 && (
              <button onClick={() => setPreviewIndex(p => p !== null ? p + 1 : null)} className="absolute right-3 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="button-preview-next">
                <ChevronRight className="h-5 w-5 text-white" />
              </button>
            )}
            {previewIndex !== null && images[previewIndex] && (
              <AdImage src={images[previewIndex]} alt={`Preview ${previewIndex + 1}`} className="max-w-full max-h-[90vh] object-contain p-4" data-testid="img-preview-full" />
            )}
          </div>
          {images.length > 1 && previewIndex !== null && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur text-xs text-white/80" data-testid="text-preview-counter">
              {previewIndex + 1} / {images.length}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldSection({ icon, label, children, accent, borderColor }: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  accent?: boolean;
  borderColor?: string;
}) {
  return (
    <Card className={`border-border/40 ${accent ? "bg-gradient-to-br from-primary/[0.03] to-transparent" : ""} ${borderColor ? `border-l-4 ${borderColor}` : ""}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-muted-foreground">{icon}</span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function FieldCard({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <Card className="border-border/40">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-1.5 mb-1.5 text-muted-foreground">
          {icon}
          <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function JsonOrListDisplay({ data }: { data: Record<string, unknown> | string[] | string | null }) {
  if (!data) return null;
  if (typeof data === "string") return <p className="text-sm leading-relaxed text-foreground/80">{data}</p>;
  if (Array.isArray(data)) {
    const items = data as unknown[];
    if (items.length === 0) return null;
    return (
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
            <span className="inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-foreground/30 shrink-0 mt-2" />
            <span className="text-foreground/80">{typeof item === "string" ? item : JSON.stringify(item)}</span>
          </li>
        ))}
      </ul>
    );
  }
  const entries = Object.entries(data);
  if (entries.length === 0) return null;
  return (
    <div className="space-y-2.5">
      {entries.map(([key, val]) => (
        <div key={key}>
          <p className="text-[10px] font-medium text-foreground/50 uppercase tracking-wider mb-0.5">{key.replace(/_/g, " ")}</p>
          {typeof val === "string" ? (
            <p className="text-sm leading-relaxed text-foreground/80">{val}</p>
          ) : Array.isArray(val) ? (
            <ul className="space-y-1 mt-0.5">
              {(val as unknown[]).map((v, i) => (
                <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
                  <span className="inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-foreground/30 shrink-0 mt-2" />
                  <span className="text-foreground/80">{typeof v === "string" ? v : JSON.stringify(v)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm leading-relaxed text-foreground/80">{JSON.stringify(val)}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function InstantUGCSection({ brandId, adUnit }: { brandId: string; adUnit: AdUnitType }) {
  const { toast } = useToast();
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>("");
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [selectedAngleId, setSelectedAngleId] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [resolvedPrompt, setResolvedPrompt] = useState<string | null>(null);
  const [inpaintOpen, setInpaintOpen] = useState(false);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [clipTotalSeconds, setClipTotalSeconds] = useState<number>(18);
  const [scriptData, setScriptData] = useState<{ script_title: string; overall_tone: string; scenes: { scene_number: number; scene_role: string; script: string }[] } | null>(null);
  const [generatingScenePrompts, setGeneratingScenePrompts] = useState(false);
  const [scenePrompts, setScenePrompts] = useState<{ scene_number: number; prompt: string | null; error?: string }[]>([]);
  const [generatingVideos, setGeneratingVideos] = useState(false);
  const [sceneVideos, setSceneVideos] = useState<{ scene_number: number; video_url: string | null; error?: string }[]>([]);

  const { data: rawPersonas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
    retry: 2,
  });
  const personas = rawPersonas ?? [];
  const { data: rawProducts } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
    retry: 2,
  });
  const products = rawProducts ?? [];
  const { data: rawAngles } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`],
    enabled: !!brandId,
    retry: 2,
  });
  const angles = rawAngles ?? [];

  const activePersonas = personas.filter(p => !p.status || p.status === "active" || p.status === "draft");

  const filteredAngles = selectedPersonaId && selectedProductId
    ? angles.filter(a => a.personaId === selectedPersonaId && a.productId === selectedProductId)
    : [];

  const handleGenerate = async () => {
    if (!adUnit.startingFramePrompt) {
      toast({ title: "No Starting Frame Prompt", description: "Add a Starting Frame Prompt to this ad unit first.", variant: "destructive" });
      return;
    }
    if (!selectedPersonaId) {
      toast({ title: "Select a persona", variant: "destructive" });
      return;
    }
    setGenerating(true);
    setGeneratedImage(null);
    setResolvedPrompt(null);
    try {
      const res = await apiRequest("POST", "/api/generate-ugc-start-frame", {
        adUnitId: adUnit.id,
        personaId: selectedPersonaId,
        productId: selectedProductId || null,
        angleId: selectedAngleId || null,
        brandId: brandId || null,
      });
      const data = await res.json();
      if (data.image_url) {
        setGeneratedImage(data.image_url);
        if (data.resolved_prompt) setResolvedPrompt(data.resolved_prompt);
        toast({ title: "Starting frame generated" });
      } else {
        throw new Error(data.message || "No image returned");
      }
    } catch (e: any) {
      toast({ title: "Generation failed", description: e.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateScript = async () => {
    if (!selectedPersonaId) {
      toast({ title: "Select a persona", variant: "destructive" });
      return;
    }
    setGeneratingScript(true);
    setScriptData(null);
    try {
      const res = await apiRequest("POST", "/api/generate-ugc-script", {
        adUnitId: adUnit.id,
        personaId: selectedPersonaId,
        productId: selectedProductId || null,
        angleId: selectedAngleId || null,
        brandId: brandId || null,
        secondsPerScene: clipTotalSeconds / 9,
      });
      const data = await res.json();
      if (data.scenes) {
        setScriptData(data);
        toast({ title: "Script generated" });
      } else {
        throw new Error(data.message || "No script returned");
      }
    } catch (e: any) {
      toast({ title: "Script generation failed", description: e.message, variant: "destructive" });
    } finally {
      setGeneratingScript(false);
    }
  };

  const handleGenerateScenePrompts = async () => {
    if (!scriptData || !resolvedPrompt) return;
    setGeneratingScenePrompts(true);
    setScenePrompts([]);
    try {
      const res = await apiRequest("POST", "/api/generate-ugc-scene-prompts", {
        resolvedPrompt,
        scenes: scriptData.scenes,
        adUnitId: adUnit.id,
        brandId: brandId || null,
      });
      const data = await res.json();
      if (data.scenePrompts) {
        setScenePrompts(data.scenePrompts);
        const successCount = data.scenePrompts.filter((s: any) => s.prompt).length;
        toast({ title: `${successCount}/${scriptData.scenes.length} scene prompts generated` });
      } else {
        throw new Error(data.message || "No prompts returned");
      }
    } catch (e: any) {
      toast({ title: "Scene prompt generation failed", description: e.message, variant: "destructive" });
    } finally {
      setGeneratingScenePrompts(false);
    }
  };

  const handleGenerateVideos = async () => {
    const validPrompts = scenePrompts.filter(sp => sp.prompt);
    if (validPrompts.length === 0 || !generatedImage) return;
    setGeneratingVideos(true);
    setSceneVideos([]);
    try {
      const res = await apiRequest("POST", "/api/generate-ugc-scene-videos", {
        imageUrl: generatedImage,
        scenePrompts: validPrompts.map(sp => ({ scene_number: sp.scene_number, prompt: sp.prompt })),
        adUnitId: adUnit.id,
        brandId: brandId || null,
        secondsPerScene: clipTotalSeconds / 9,
      });
      const data = await res.json();
      if (data.sceneVideos) {
        setSceneVideos(data.sceneVideos);
        const successCount = data.sceneVideos.filter((v: any) => v.video_url).length;
        toast({ title: `${successCount}/${validPrompts.length} scene videos generated` });
      } else {
        throw new Error(data.message || "No videos returned");
      }
    } catch (e: any) {
      toast({ title: "Video generation failed", description: e.message, variant: "destructive" });
    } finally {
      setGeneratingVideos(false);
    }
  };

  const roleColors: Record<string, string> = {
    hook: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
    story: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
    close: "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20",
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-primary" />
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Instant UGC Generation</h2>
      </div>
      <Card className="border-border/40">
        <CardContent className="pt-5 pb-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> Persona</label>
              <Select value={selectedPersonaId} onValueChange={(v) => { setSelectedPersonaId(v); setSelectedProductId(""); setSelectedAngleId(""); }}>
                <SelectTrigger className="h-9 text-sm" data-testid="select-ugc-persona"><SelectValue placeholder="Select persona..." /></SelectTrigger>
                <SelectContent>
                  {activePersonas.length > 0
                    ? activePersonas.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)
                    : <div className="px-2 py-1.5 text-xs text-muted-foreground" data-testid="text-ugc-personas-empty">No personas yet for this brand.</div>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Package className="h-3 w-3" /> Product</label>
              <Select value={selectedProductId} onValueChange={(v) => { setSelectedProductId(v); setSelectedAngleId(""); }} disabled={!selectedPersonaId}>
                <SelectTrigger className="h-9 text-sm" data-testid="select-ugc-product"><SelectValue placeholder={selectedPersonaId ? "Select product..." : "Select persona first"} /></SelectTrigger>
                <SelectContent>
                  {products.length > 0
                    ? products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)
                    : <div className="px-2 py-1.5 text-xs text-muted-foreground" data-testid="text-ugc-products-empty">No products yet for this brand.</div>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><BookOpen className="h-3 w-3" /> Narrative Angle</label>
              <Select value={selectedAngleId} onValueChange={setSelectedAngleId} disabled={!selectedPersonaId || !selectedProductId}>
                <SelectTrigger className="h-9 text-sm" data-testid="select-ugc-angle"><SelectValue placeholder={selectedProductId ? "Select angle..." : "Select product first"} /></SelectTrigger>
                <SelectContent>
                  {renderGroupedAngleItems(filteredAngles)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ad Length</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[18, 36, 54].map((secs) => (
                <button
                  key={secs}
                  type="button"
                  onClick={() => setClipTotalSeconds(secs)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                    clipTotalSeconds === secs
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border/60 bg-background hover-elevate"
                  }`}
                  data-testid={`button-clip-length-${secs}`}
                >
                  {secs}s
                </button>
              ))}
              <span className="text-[11px] text-muted-foreground" data-testid="text-clip-length-detail">
                9 scenes &times; {clipTotalSeconds / 9}s each
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={handleGenerate}
              disabled={generating || !selectedPersonaId || !adUnit.startingFramePrompt}
              className="rounded-full gap-2"
              data-testid="button-generate-start-frame"
            >
              {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating Starting Frame...</> : <><Sparkles className="h-4 w-4" /> Generate Starting Frame</>}
            </Button>
            {!adUnit.startingFramePrompt && (
              <p className="text-xs text-muted-foreground">Add a Starting Frame Prompt above to enable generation.</p>
            )}
          </div>

          {resolvedPrompt && (
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Resolved Prompt</label>
              <Textarea value={resolvedPrompt} readOnly rows={5} className="font-mono text-xs resize-y opacity-70" data-testid="textarea-ugc-resolved-prompt" />
            </div>
          )}

          {generatedImage && (
            <div className="space-y-3 pt-2">
              <div className="relative rounded-xl border overflow-hidden bg-muted/30 max-w-xs">
                <img src={generatedImage} alt="Generated UGC starting frame" className="w-full h-auto" data-testid="img-ugc-start-frame" />
              </div>
              <div className="flex gap-2">
                <a href={generatedImage} target="_blank" rel="noopener noreferrer" download>
                  <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="button-download-ugc-frame">
                    <Download className="h-3.5 w-3.5" /> Open Full Size
                  </Button>
                </a>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full gap-1.5"
                  onClick={() => setInpaintOpen(true)}
                  data-testid="button-edit-ugc-frame"
                >
                  <Paintbrush className="h-3.5 w-3.5" /> Edit Image
                </Button>
                <InpaintingModal
                  open={inpaintOpen}
                  onClose={() => setInpaintOpen(false)}
                  imageUrl={generatedImage}
                  brandId={brandId}
                  onAccept={(newUrl) => {
                    setGeneratedImage(newUrl);
                    setInpaintOpen(false);
                  }}
                />
              </div>
            </div>
          )}

          {generatedImage && (
            <div className="border-t border-border/30 pt-4 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  onClick={handleGenerateScript}
                  disabled={generatingScript || !selectedPersonaId}
                  className="rounded-full gap-2"
                  data-testid="button-generate-script"
                >
                  {generatingScript ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating Script...</> : <><ScrollText className="h-4 w-4" /> Generate Script</>}
                </Button>
              </div>
            </div>
          )}

          {scriptData && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-3">
                <Clapperboard className="h-4 w-4 text-primary" />
                <div>
                  <h3 className="text-sm font-semibold" data-testid="text-script-title">{scriptData.script_title}</h3>
                  <p className="text-xs text-muted-foreground">Tone: {scriptData.overall_tone}</p>
                </div>
              </div>
              <div className="space-y-2">
                {scriptData.scenes.map((scene) => {
                  const sp = scenePrompts.find(p => p.scene_number === scene.scene_number);
                  return (
                    <div
                      key={scene.scene_number}
                      className={`rounded-lg border p-3 ${roleColors[scene.scene_role] || "bg-muted/30 border-border/30"}`}
                      data-testid={`scene-card-${scene.scene_number}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">Scene {scene.scene_number}</span>
                        <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-background/50 border border-current/10">{scene.scene_role}</span>
                      </div>
                      <p className="text-sm leading-relaxed">{scene.script}</p>
                      {sp && sp.prompt && (
                        <div className="mt-2 pt-2 border-t border-current/10">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Film className="h-3 w-3 opacity-60" />
                            <span className="text-[10px] font-semibold uppercase tracking-wider opacity-60">Kling Prompt</span>
                          </div>
                          <p className="text-xs leading-relaxed opacity-80 whitespace-pre-wrap">{sp.prompt}</p>
                        </div>
                      )}
                      {sp && sp.error && (
                        <div className="mt-2 pt-2 border-t border-current/10">
                          <p className="text-xs text-red-500">Failed: {sp.error}</p>
                        </div>
                      )}
                      {(() => {
                        const sv = sceneVideos.find(v => v.scene_number === scene.scene_number);
                        if (!sv) return null;
                        if (sv.video_url) return (
                          <div className="mt-2 pt-2 border-t border-current/10">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <Film className="h-3 w-3 opacity-60" />
                              <span className="text-[10px] font-semibold uppercase tracking-wider opacity-60">Generated Video</span>
                            </div>
                            <video
                              src={sv.video_url}
                              controls
                              playsInline
                              className="rounded-lg w-full max-w-[240px]"
                              data-testid={`video-scene-${scene.scene_number}`}
                            />
                            <a
                              href={sv.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 mt-1.5 text-xs text-primary hover:underline"
                              data-testid={`download-video-${scene.scene_number}`}
                            >
                              <Download className="h-3 w-3" /> Download
                            </a>
                          </div>
                        );
                        if (sv.error) return (
                          <div className="mt-2 pt-2 border-t border-current/10">
                            <p className="text-xs text-red-500">Video failed: {sv.error}</p>
                          </div>
                        );
                        return null;
                      })()}
                    </div>
                  );
                })}
              </div>

              <div className="pt-3 border-t border-border/30 flex items-center gap-3 flex-wrap">
                <Button
                  onClick={handleGenerateScenePrompts}
                  disabled={generatingScenePrompts}
                  className="rounded-full gap-2"
                  data-testid="button-generate-scene-prompts"
                >
                  {generatingScenePrompts ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating {scriptData.scenes.length} Scene Prompts...</> : <><Film className="h-4 w-4" /> Generate All Scene Prompts</>}
                </Button>
                {scenePrompts.some(sp => sp.prompt) && (
                  <Button
                    onClick={handleGenerateVideos}
                    disabled={generatingVideos || !generatedImage}
                    className="rounded-full gap-2"
                    variant="default"
                    data-testid="button-generate-videos"
                  >
                    {generatingVideos ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating {scenePrompts.filter(sp => sp.prompt).length} Scene Videos...</> : <><Clapperboard className="h-4 w-4" /> Generate Scenes</>}
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const ASPECT_RATIOS: AspectRatio[] = [...AD_ASPECT_RATIOS];
type RatioResult = {
  generatedImage: string | null;
  openaiImage: string | null;
  falError: string | null;
  openaiError: string | null;
  savingNb: boolean;
  savedNb: boolean;
  savingOa: boolean;
  savedOa: boolean;
  inpaintOpen: boolean;
  inpaintOpenOa: boolean;
};
const emptyRatioResult: RatioResult = {
  generatedImage: null,
  openaiImage: null,
  falError: null,
  openaiError: null,
  savingNb: false,
  savedNb: false,
  savingOa: false,
  savedOa: false,
  inpaintOpen: false,
  inpaintOpenOa: false,
};

function InstantAdGenSection({ brandId, adUnit }: { brandId: string; adUnit: AdUnitType }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = !!user?.isMaster || !!user?.isSuperuser;
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>("");
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  // Task #744 — optional product variant selection. null = "no specific variant"
  // (legacy byte-for-byte path).
  const [selectedVariantName, setSelectedVariantName] = useState<string | null>(null);
  const [selectedOfferingId, setSelectedOfferingId] = useState<string>("");
  const [selectedAngleId, setSelectedAngleId] = useState<string>("");
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [promptGenerated, setPromptGenerated] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [openaiModel, setOpenaiModel] = useState<string>("gpt-image-2");
  const [compareModels, setCompareModels] = useState(false);
  const [useNewPromptMaker, setUseNewPromptMaker] = useState(false);
  // Admin-only "Prompt Maker v2" toggle — routes the Generate Image Prompt
  // call to the v2 Sim workflow (the user-refs clone Ads Express uses).
  // Standalone: no interaction with the existing toggles; server gives v2
  // precedence if both flags arrive.
  const [usePromptMakerV2, setUsePromptMakerV2] = useState(false);
  // v2 sub-toggle — default OFF = the no-logo v2 workflow (prompt never
  // references a logo; product re-indexed Image 3 → Image 2). ON opts in
  // to the with-logo v2 workflow and keeps the logo reference image.
  const [v2IncludeLogo, setV2IncludeLogo] = useState(false);
  // Aspect-ratio picker. The user can check 1, 2, or all 3; we fan out one
  // /api/generate-ad-image request per checked ratio in parallel and render
  // each tile independently.
  const RATIO_LABELS: Record<AspectRatio, string> = Object.fromEntries(
    AD_ASPECT_RATIO_OPTIONS.map((o) => [o.value, `${o.dimensions} (${o.label})`]),
  ) as Record<AspectRatio, string>;
  const RATIO_TESTID: Record<AspectRatio, string> = Object.fromEntries(
    AD_ASPECT_RATIO_OPTIONS.map((o) => [o.value, o.value.replace(":", "x")]),
  ) as Record<AspectRatio, string>;
  const [selectedRatios, setSelectedRatios] = useState<AspectRatio[]>(() => loadLastAspectRatios());
  // Base ratio = the one rendered fresh from the prompt + product/logo refs.
  // Any other checked ratios are then "reframed" from the base via
  // nano-banana-2/edit (passing the base image as the only reference) so all
  // tiles share one consistent hero shot rather than three independent rolls.
  const [baseRatio, setBaseRatio] = useState<AspectRatio>(() => loadLastAspectRatio());
  const [results, setResults] = useState<Record<string, RatioResult>>({});
  const [resolvedPrompt, setResolvedPrompt] = useState<string>("");
  // Creative Mode (Precise / Balanced / Creative Freedom). Default is
  // overridden once the active modifier list arrives (whichever row has
  // is_default=true). Falls back to "balanced" if the fetch fails.
  const [creativeStyleMode, setCreativeStyleMode] = useState<string>("balanced");
  const [uploadingExample, setUploadingExample] = useState(false);
  const exampleFileRef = useRef<HTMLInputElement>(null);

  const includeProductImage = (adUnit as any).adgenIncludeProductImage !== false;
  const exampleImageUrl: string | null = (adUnit as any).adgenExampleImageUrl || null;
  const isMeme = (adUnit as any).isMeme === true;
  const memeReferenceImageUrl: string | null = (adUnit as any).memeReferenceImageUrl || null;
  const [uploadingMemeRef, setUploadingMemeRef] = useState(false);
  const memeRefFileRef = useRef<HTMLInputElement>(null);

  const handleMemeRefUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMemeRef(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", body: formData, credentials: "include" });
      const data = await res.json();
      if (data.url || data.falUrl) {
        await updateAdUnitSetting({ memeReferenceImageUrl: data.url || data.falUrl });
        toast({ title: "Meme reference image uploaded" });
      }
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploadingMemeRef(false);
      if (memeRefFileRef.current) memeRefFileRef.current.value = "";
    }
  };

  const updateAdUnitSetting = async (patch: Record<string, unknown>) => {
    try {
      await apiRequest("PATCH", `/api/ad-units/${adUnit.id}`, patch);
      queryClient.invalidateQueries({ queryKey: ["/api/ad-units", adUnit.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/ad-units"] });
    } catch (e: any) {
      toast({ title: "Update failed", description: e.message, variant: "destructive" });
    }
  };

  const handleExampleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingExample(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", body: formData, credentials: "include" });
      const data = await res.json();
      if (data.url || data.falUrl) {
        await updateAdUnitSetting({ adgenExampleImageUrl: data.url || data.falUrl });
        toast({ title: "Example image uploaded" });
      }
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploadingExample(false);
      if (exampleFileRef.current) exampleFileRef.current.value = "";
    }
  };

  const { data: rawPersonas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
    retry: 2,
  });
  const personas = rawPersonas ?? [];
  const { data: rawProducts } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
    retry: 2,
  });
  const products = rawProducts ?? [];
  type CreativeStyleModifierRow = {
    id: string; modeKey: string; displayName: string; description: string | null;
    prependText: string; includeTemplatePrompt: boolean; isDefault: boolean; sortOrder: number;
  };
  const FALLBACK_CREATIVE_STYLES: CreativeStyleModifierRow[] = [
    { id: "fb-precise", modeKey: "precise", displayName: "Precise", description: null, prependText: "", includeTemplatePrompt: true, isDefault: false, sortOrder: 10 },
    { id: "fb-balanced", modeKey: "balanced", displayName: "Balanced", description: null, prependText: "", includeTemplatePrompt: true, isDefault: true, sortOrder: 20 },
    { id: "fb-creative", modeKey: "creative_freedom", displayName: "Creative Freedom", description: null, prependText: "", includeTemplatePrompt: false, isDefault: false, sortOrder: 30 },
  ];
  const { data: creativeStylesData, isSuccess: creativeStylesLoaded } = useQuery<CreativeStyleModifierRow[]>({
    queryKey: ["/api/creative-style-modifiers"],
    retry: 1,
  });
  // While the fetch is in flight, render with the fallback triplet so the
  // selector never flashes empty; the SQL-driven default initialization
  // below only fires once real data has resolved (or the query has failed).
  const creativeStyles: CreativeStyleModifierRow[] =
    creativeStylesData && creativeStylesData.length > 0
      ? creativeStylesData
      : FALLBACK_CREATIVE_STYLES;
  // Static, hardcoded helper text per the spec — independent of SQL `description`
  // so wording stays consistent if an admin tweaks the DB row in the future.
  const CREATIVE_MODE_HELPER_TEXT: Record<string, string> = {
    precise: "Closely matches the reference image, changing only what the ad inputs require.",
    balanced: "Balances template structure with natural creative adaptation.",
    creative_freedom: "Uses the reference as inspiration to explore fresher, less constrained concepts.",
  };
  const CREATIVE_MODE_LABEL_SUFFIX: Record<string, string> = {
    creative_freedom: " ✨",
  };
  // Initialize from the SQL `is_default=true` row once the query resolves.
  // On fetch error we keep the existing "balanced" default from useState.
  useEffect(() => {
    if (!creativeStylesLoaded || !creativeStylesData || creativeStylesData.length === 0) return;
    const dflt = creativeStylesData.find(s => s.isDefault)
      ?? creativeStylesData.find(s => s.modeKey === "balanced")
      ?? creativeStylesData[0];
    if (dflt) setCreativeStyleMode(prev => (prev === "balanced" ? dflt.modeKey : prev));
    // Only run on first successful load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creativeStylesLoaded]);
  const { data: rawOffers } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
    retry: 2,
  });
  const offers = rawOffers ?? [];
  const { data: rawAngles } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`],
    enabled: !!brandId,
    retry: 2,
  });
  const angles = rawAngles ?? [];
  const { data: brand } = useQuery<Brand>({
    queryKey: [`/api/brands/${brandId}`],
    enabled: !!brandId,
    retry: 2,
  });
  // Task #1517 — safe_zone_reframe grant for the current user on this brand.
  // Granted: saves from this page pass the tile's aspect ratio through so
  // the persisted image is normalized to exact advertised dimensions and the
  // library row records it. Not granted (default) = legacy save body.
  const { data: betaFlags } = useQuery<Record<string, boolean>>({
    queryKey: [`/api/brands/${brandId}/beta-features`],
    enabled: !!brandId,
  });
  const safeZoneReframeEnabled = betaFlags?.safe_zone_reframe === true;

  const filteredAngles = selectedPersonaId && selectedProductId
    ? angles.filter(a => a.personaId === selectedPersonaId && a.productId === selectedProductId)
    : selectedPersonaId && selectedOfferingId
      ? angles.filter(a => a.personaId === selectedPersonaId && (a as any).offeringId === selectedOfferingId)
      : angles;

  const selectedPersona = personas.find(p => p.id === selectedPersonaId);
  const selectedOffering = offers.find(o => o.id === selectedOfferingId);
  const selectedProduct = products.find(p => p.id === selectedProductId);
  const selectedAngle = angles.find(a => a.id === selectedAngleId);

  const buildPrompt = useCallback(() => {
    const template = adUnit.imagePromptTemplate || "";
    if (!template) return "";
    let prompt = template;
    if (selectedProduct) {
      prompt = prompt.replace(/\[YOUR PRODUCT\]/gi, selectedProduct.name);
      prompt = prompt.replace(/\[YOUR BRAND\]/gi, "");
      prompt = prompt.replace(/\[PRODUCT\]/gi, selectedProduct.name);
      prompt = prompt.replace(/\[BRAND\]/gi, "");
      const category = (selectedProduct as any).category || "";
      prompt = prompt.replace(/\[COMPETITOR CATEGORY\]/gi, category);
      prompt = prompt.replace(/\[PRIMARY BRAND COLOR\]/gi, "");
      const ingredients = (selectedProduct.ingredientsOrFeatures as string[] | null) || [];
      const strengths = ingredients.slice(0, 5).map((s, i) => `${i + 1}. ${typeof s === "string" ? s : JSON.stringify(s)}`).join(", ");
      prompt = prompt.replace(/\[STRENGTH 1-5\]/gi, strengths || "quality, natural, effective, trusted, premium");
      prompt = prompt.replace(/\[WEAKNESS 1-5\]/gi, "synthetic ingredients, harsh chemicals, unknown sourcing, filler ingredients, artificial fragrance");
    }
    if (selectedPersona) {
      prompt = prompt.replace(/\[PERSONA\]/gi, selectedPersona.name);
      prompt = prompt.replace(/\[DESIRE\]/gi, (selectedPersona as any).desire || "");
      prompt = prompt.replace(/\[PAIN\]/gi, (selectedPersona as any).pain || "");
    }
    if (selectedAngle) {
      prompt = prompt.replace(/\[ANGLE\]/gi, selectedAngle.name);
      prompt = prompt.replace(/\[NARRATIVE\]/gi, selectedAngle.summary || selectedAngle.name);
      prompt = prompt.replace(/\[CORE TENSION\]/gi, selectedAngle.coreTension || "");
    }
    return prompt;
  }, [adUnit.imagePromptTemplate, selectedProduct, selectedPersona, selectedAngle]);

  // Seed the textarea with a template-substituted preview *only* before the
  // user has generated a real NB prompt. Once promptGenerated=true, the
  // AI-generated prompt owns the textarea and we must NOT silently overwrite
  // it on every query refetch / dep change (that was producing stale, raw-
  // template generations).
  useEffect(() => {
    if (promptGenerated) return;
    setResolvedPrompt(buildPrompt());
  }, [buildPrompt, promptGenerated]);

  // If the user changes persona / product / angle after generating an NB
  // prompt, the prompt is now stale — clear it and require a fresh
  // "Generate NB Prompt" click before they can generate an image.
  const lastSelectionKey = useRef<string>("");
  useEffect(() => {
    const key = `${selectedPersonaId}|${selectedProductId}|${selectedAngleId}`;
    if (lastSelectionKey.current && lastSelectionKey.current !== key && promptGenerated) {
      setPromptGenerated(false);
      setResolvedPrompt("");
      toast({
        title: "Prompt cleared",
        description: "Persona, product, or angle changed — regenerate the NB prompt before generating images.",
      });
    }
    lastSelectionKey.current = key;
  }, [selectedPersonaId, selectedProductId, selectedAngleId, promptGenerated, toast]);

  const updateRatioResult = (ratio: AspectRatio, patch: Partial<RatioResult>) => {
    setResults(prev => ({
      ...prev,
      [ratio]: { ...(prev[ratio] ?? emptyRatioResult), ...patch },
    }));
  };

  const handleGenerate = async () => {
    if (!resolvedPrompt.trim()) {
      toast({ title: "No prompt", description: "Add a prompt template to this ad unit first.", variant: "destructive" });
      return;
    }
    if (selectedRatios.length === 0) {
      toast({ title: "No aspect ratio", description: "Pick at least one aspect ratio.", variant: "destructive" });
      return;
    }
    // Base must be one of the checked ratios. If the current base was
    // unchecked, fall back to the first checked one.
    const effectiveBase: AspectRatio = selectedRatios.includes(baseRatio)
      ? baseRatio
      : selectedRatios[0];
    const derivedRatios = selectedRatios.filter((r) => r !== effectiveBase);

    // Remember the user's selection across visits.
    saveLastAspectRatios(selectedRatios);
    saveLastAspectRatio(effectiveBase);

    setGenerating(true);
    // Reset results entirely — only the currently-checked ratios should
    // appear. Stale tiles from a previous run (e.g. a 9:16 you've since
    // unchecked) must not linger alongside the new render.
    setResults(() => {
      const next: Record<string, RatioResult> = {};
      for (const r of selectedRatios) next[r] = { ...emptyRatioResult };
      return next;
    });
    try {
      const referenceImages: { url: string; label: string }[] = [];
      if (isMeme) {
        if (memeReferenceImageUrl) referenceImages.push({ url: memeReferenceImageUrl, label: `meme reference image for "${adUnit.name}"` });
      } else {
        // Fixed, prompt-friendly ordering — prompts refer to these by
        // index ("Image 1 is the reference ad, Image 2 is the brand
        // logo, Image 3 is the product"). Do not reorder.
        //
        // Prompt Maker v2 without "Include logo": the no-logo v2 workflow
        // writes prompts that never reference a logo and re-index the
        // product Image 3 → Image 2, so the logo ref must be omitted and
        // the product relabeled to match. Every other configuration keeps
        // the legacy ordering byte-identical.
        const v2NoLogo = isAdmin && usePromptMakerV2 && !v2IncludeLogo;
        // Image 1 — Reference ad layout (the ad-unit's example image)
        if (exampleImageUrl) referenceImages.push({ url: exampleImageUrl, label: `Image 1 — reference ad layout for "${adUnit.name}"` });
        // Image 2 — Brand logo (omitted on v2 no-logo runs)
        if (!v2NoLogo) {
          if (brand?.adgenLogoUrl) referenceImages.push({ url: brand.adgenLogoUrl, label: `Image 2 — brand logo for "${brand.name}"` });
          else if (brand?.logoUrl) referenceImages.push({ url: brand.logoUrl, label: `Image 2 — brand logo for "${brand.name}"` });
        }
        // Image 3 — Product photo (only when a physical product is
        // selected; offerings deliberately have no Image 3). On v2
        // no-logo runs the product becomes Image 2.
        if (includeProductImage && selectedProduct) {
          const refs = (selectedProduct as any).adgenImageRefs;
          if (Array.isArray(refs)) {
            const firstProductRef = refs.find((u: unknown) => typeof u === "string" && (u as string).length > 0) as string | undefined;
            if (firstProductRef) {
              referenceImages.push({ url: firstProductRef, label: `Image ${v2NoLogo ? 2 : 3} — product photo of "${(selectedProduct as any).name}"` });
            }
          }
        }
      }
      console.log("[InstantAdGen] Reference images:", referenceImages.length, referenceImages.map(r => r.label));

      // Task #2213 — truest-pixels multi-ratio flow for a 9:16 base: render
      // the CLEAN 4:5 base creative once, mechanically band-extend it into
      // the 9:16 deliverable (same safe-zone extender as every other 9:16
      // static-ad pipeline), reuse the 4:5 base directly for a checked 4:5
      // tile (no second generative pass), and reframe the other derived
      // ratios from the clean 4:5 — never from a banded 9:16 canvas.
      // Single-ratio 9:16 runs, memes, and admin compare-models runs keep
      // today's direct-render behavior byte-identical.
      const { useCleanBase45, reuse45Tile, generativeDerived } = planInstantAdMultiRatio({
        effectiveBase,
        derivedRatios,
        isMeme,
        compareModels: isAdmin && compareModels,
      });

      // ── Phase 1: render the base ratio fresh from the prompt + product/logo refs.
      let baseImageUrl: string | null = null;
      let baseSucceeded = false;
      // Whether the BASE-ratio tile itself has an image (on the clean-4:5
      // path the 9:16 tile can fail at the extension step even though the
      // 4:5 base render succeeded — the toast counts tiles, not sources).
      let baseTileSucceeded = false;
      if (useCleanBase45) {
        let base45Url: string | null = null;
        try {
          const res = await apiRequest("POST", "/api/generate-ad-image", {
            prompt: resolvedPrompt,
            reference_images: referenceImages,
            brandId: brandId || null,
            productId: selectedProductId || null,
            selectedVariantName: selectedProductId ? selectedVariantName : null,
            includeProductImage: includeProductImage && !isMeme,
            compareModels: false,
            aspect_ratio: "4:5",
          });
          const data = await res.json();
          if (data.image_url) base45Url = data.image_url as string;
          else updateRatioResult(effectiveBase, { falError: "No image returned" });
        } catch (e: any) {
          updateRatioResult(effectiveBase, { falError: e?.message || String(e) });
        }
        if (base45Url) {
          // The clean 4:5 base is the source for every derived reframe.
          baseImageUrl = base45Url;
          baseSucceeded = true;
          // A checked 4:5 tile reuses the base creative directly — the
          // truest pixels, no second generative pass.
          if (reuse45Tile) {
            updateRatioResult("4:5", { generatedImage: base45Url });
          }
          // The 9:16 deliverable = mechanical band extension of the base.
          try {
            const extRes = await apiRequest("POST", "/api/generate-ad-image/extend-916", {
              imageUrl: base45Url,
              brandId: brandId || null,
            });
            const extData = await extRes.json();
            if (extData.image_url) {
              updateRatioResult("9:16", { generatedImage: extData.image_url });
              baseTileSucceeded = true;
            } else {
              updateRatioResult("9:16", { falError: "No image returned from band extension" });
            }
          } catch (e: any) {
            updateRatioResult("9:16", { falError: e?.message || String(e) });
          }
        }
      } else try {
        const res = await apiRequest("POST", "/api/generate-ad-image", {
          prompt: resolvedPrompt,
          reference_images: referenceImages,
          brandId: brandId || null,
          productId: selectedProductId || null,
          // Task #744 — selected variant's picture becomes the primary product ref.
          selectedVariantName: selectedProductId ? selectedVariantName : null,
          includeProductImage: includeProductImage && !isMeme,
          compareModels: isAdmin && compareModels,
          aspect_ratio: effectiveBase,
        });
        const data = await res.json();
        if (data.compare) {
          updateRatioResult(effectiveBase, {
            generatedImage: data.image_url || null,
            openaiImage: data.openai_image_url || null,
            falError: data.fal_error || null,
            openaiError: data.openai_error || null,
          });
          if (data.openai_model) setOpenaiModel(data.openai_model);
          if (data.image_url) { baseImageUrl = data.image_url; baseSucceeded = true; }
        } else if (data.image_url) {
          updateRatioResult(effectiveBase, { generatedImage: data.image_url });
          baseImageUrl = data.image_url;
          baseSucceeded = true;
        } else {
          updateRatioResult(effectiveBase, { falError: "No image returned" });
        }
        baseTileSucceeded = baseSucceeded;
      } catch (e: any) {
        updateRatioResult(effectiveBase, { falError: e?.message || String(e) });
      }

      // ── Phase 2: reframe the base into every other checked ratio in parallel.
      // We pass ONLY the base image as the reference (no logo/product refs)
      // and a tight reframing prompt so nano-banana-2/edit preserves the
      // exact subject, lighting, colors, and composition while adapting the
      // canvas to the new aspect ratio. Compare-Models is intentionally off
      // for derived calls — we only compare on the base.
      // Task #2213 — on the clean-4:5-base path, a checked 4:5 tile was
      // already filled from the base creative (no generative pass) and the
      // 9:16 tile from the mechanical extension, so only the remaining
      // ratios reframe generatively — always from the clean 4:5 base.
      let derivedSuccess = baseSucceeded && reuse45Tile ? 1 : 0;
      if (baseSucceeded && baseImageUrl && generativeDerived.length > 0) {
        const reframePrompt =
          `Reframe this exact ad image at a new aspect ratio. ` +
          `Keep the subject, characters, product, layout, typography, lighting, ` +
          `colors, and overall composition identical to the reference image. ` +
          `Only adapt the framing and background to fill the new canvas naturally. ` +
          `Do not invent a different scene, do not change products or copy. ` +
          `Original creative direction (for context): ${resolvedPrompt}`;
        const settled = await Promise.allSettled(
          generativeDerived.map(async (ratio) => {
            const res = await apiRequest("POST", "/api/generate-ad-image", {
              prompt: reframePrompt,
              reference_images: [{ url: baseImageUrl!, label: `base ${useCleanBase45 ? "4:5" : effectiveBase} ad image to reframe at ${ratio}` }],
              brandId: brandId || null,
              productId: selectedProductId || null,
              includeProductImage: false,
              compareModels: false,
              aspect_ratio: ratio,
            });
            const data = await res.json();
            return { ratio, data };
          }),
        );
        for (let i = 0; i < settled.length; i++) {
          const result = settled[i];
          const ratio = generativeDerived[i];
          if (result.status === "fulfilled") {
            const { data } = result.value;
            if (data.image_url) {
              updateRatioResult(ratio, { generatedImage: data.image_url });
              derivedSuccess++;
            } else {
              updateRatioResult(ratio, { falError: data.fal_error || "No image returned" });
            }
          } else {
            updateRatioResult(ratio, { falError: result.reason?.message || String(result.reason) });
          }
        }
      } else if (!baseSucceeded && derivedRatios.length > 0) {
        // Base failed → mark every derived tile as skipped.
        for (const ratio of derivedRatios) {
          updateRatioResult(ratio, { falError: `Skipped: base ${effectiveBase} failed` });
        }
      }

      const successCount = (baseTileSucceeded ? 1 : 0) + derivedSuccess;
      toast({
        title: successCount === selectedRatios.length ? "Images generated" : successCount === 0 ? "Generation failed" : "Partial generation",
        description: `${successCount}/${selectedRatios.length} generated`,
        variant: successCount === 0 ? "destructive" : "default",
      });
    } catch (e: any) {
      toast({ title: "Generation failed", description: e.message, variant: "destructive" });
    } finally { setGenerating(false); }
  };

  // Saves the current Nano-Banana-2 or OpenAI image into the My Ads library
  // by hitting the new /library/save-generated-image endpoint. The endpoint
  // creates the parent ad_generations + child generated_ads rows with
  // isSaved=true and persists the image to R2 in one shot.
  const handleSaveToLibrary = async (
    ratio: AspectRatio,
    source: "nb" | "oa",
    imageUrl: string,
    modelLabel: string,
  ) => {
    if (!brandId) return;
    updateRatioResult(ratio, source === "nb" ? { savingNb: true } : { savingOa: true });
    try {
      await apiRequest("POST", `/api/brands/${brandId}/library/save-generated-image`, {
        imageUrl,
        adUnitId: adUnit.id,
        personaId: selectedPersonaId || null,
        productId: selectedProductId || null,
        narrativeAngleId: selectedAngleId || null,
        prompt: resolvedPrompt || null,
        modelName: modelLabel,
        // Task #1517 — only when the safe_zone_reframe grant is active;
        // omitted otherwise so ungranted saves stay byte-identical.
        ...(safeZoneReframeEnabled ? { aspectRatio: ratio } : {}),
      });
      updateRatioResult(ratio, source === "nb"
        ? { savingNb: false, savedNb: true }
        : { savingOa: false, savedOa: true });
      // Refresh every filtered and paginated My Ads view if the user navigates there next.
      invalidateGeneratedAdListings();
      toast({ title: "Saved to My Ads", description: "Image is now in your library." });
    } catch (e: any) {
      updateRatioResult(ratio, source === "nb" ? { savingNb: false } : { savingOa: false });
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    }
  };

  const handleGeneratePrompt = async () => {
    setGeneratingPrompt(true);
    try {
      // Resolve the ad-unit's example image (used by the New Prompt Maker
      // workflow as the visual reference). Prefer the dedicated adgen
      // example field; fall back to the first entry of the exampleImages
      // gallery if that's empty.
      const firstExample = toImageRecords((adUnit as any).exampleImages)[0];
      const exampleImageUrl = (adUnit as any).adgenExampleImageUrl
        || (firstExample ? displayUrl(firstExample) : null);
      const payload: Record<string, unknown> = {
        adUnit: {
          name: adUnit.name,
          description: adUnit.strategicRole,
          imagePromptTemplate: adUnit.imagePromptTemplate,
          formatType: adUnit.formatType,
          funnelDeployment: adUnit.funnelDeployment,
          psychologicalFunction: adUnit.psychologicalFunction,
          structuralDrivers: adUnit.structuralDrivers,
          copyFramework: adUnit.copyFramework,
          exampleImageUrl,
        },
      };
      if (selectedPersona) {
        payload.persona = {
          name: selectedPersona.name,
          psychographicProfile: (selectedPersona as any).psychographicProfile,
          demographicSnapshot: (selectedPersona as any).demographicSnapshot,
          coreDesire: (selectedPersona as any).coreDesire,
          identityStatement: (selectedPersona as any).identityStatement,
          buyingTriggers: (selectedPersona as any).buyingTriggers,
          mediaPreferences: (selectedPersona as any).mediaPreferences,
        };
      }
      if (selectedProduct) {
        payload.product = {
          name: selectedProduct.name,
          headline: (selectedProduct as any).headline,
          ingredientsOrFeatures: selectedProduct.ingredientsOrFeatures,
          uniqueMechanism: (selectedProduct as any).uniqueMechanism,
          category: (selectedProduct as any).category,
        };
      }
      if (selectedOffering) {
        payload.offering = {
          name: selectedOffering.name,
          offerType: selectedOffering.offerType,
          offerCategory: selectedOffering.offerCategory,
          summary: selectedOffering.summary,
          shortDescription: selectedOffering.shortDescription,
          valueFraming: selectedOffering.valueFraming,
          urgencyFraming: selectedOffering.urgencyFraming,
          buyerProblem: selectedOffering.buyerProblem,
          desiredOutcome: selectedOffering.desiredOutcome,
          valueProposition: selectedOffering.valueProposition,
          offerDetails: selectedOffering.offerDetails,
          fulfillment: selectedOffering.fulfillment,
          proof: selectedOffering.proof,
          riskReversal: selectedOffering.riskReversal,
          objections: selectedOffering.objections,
          conversion: selectedOffering.conversion,
          creativeInputs: selectedOffering.creativeInputs,
        };
      }
      if (selectedAngle) {
        payload.narrativeAngle = { name: selectedAngle.name, summary: selectedAngle.summary, coreTension: selectedAngle.coreTension, family: selectedAngle.family };
      }
      if (brand) {
        payload.brand = { name: brand.name, category: brand.category, positioning: brand.positioning, enemy: brand.enemy, promise: brand.promise, colorPalette: brand.colorPalette };
      }
      // Forward the chosen Creative Style mode so the server can prepend the
      // modifier text and (for Creative Freedom) drop the rigid template.
      payload.creativeStyleMode = creativeStyleMode;
      // Task #744 — apply selected variant's ingredients/description + picture.
      payload.selectedVariantName = selectedProductId ? selectedVariantName : null;
      if (isAdmin && useNewPromptMaker) payload.useNewPromptMaker = true;
      if (isAdmin && usePromptMakerV2) {
        payload.usePromptMakerV2 = true;
        if (v2IncludeLogo) payload.v2IncludeLogo = true;
      }
      const res = await apiRequest("POST", "/api/generate-nb-prompt", payload);
      const data = await res.json();
      if (data.prompt) { setResolvedPrompt(data.prompt); setPromptGenerated(true); toast({ title: "Image Prompt Generated" }); }
      else throw new Error("No prompt returned");
    } catch (e: any) {
      toast({ title: "Prompt generation failed", description: e.message, variant: "destructive" });
    } finally { setGeneratingPrompt(false); }
  };

  const activePersonas = personas.filter(p => !p.status || p.status === "active" || p.status === "draft");
  const activeProducts = products.filter(p => p.status === "active");

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Instant Ad Generation</h2>
      </div>
      <Card className="border-border/40">
        <CardContent className="pt-5 pb-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> Persona</label>
              <SearchableSelect
                value={selectedPersonaId}
                onValueChange={(v) => { setSelectedPersonaId(v); setSelectedAngleId(""); }}
                placeholder="Select persona..."
                testId="select-persona"
                options={activePersonas.map(p => ({ value: p.id, label: p.name }))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Package className="h-3 w-3" /> Product</label>
              <SearchableSelect
                value={selectedProductId}
                onValueChange={(v) => {
                  setSelectedProductId(v);
                  setSelectedVariantName(null);
                  setSelectedOfferingId("");
                  setSelectedAngleId("");
                }}
                disabled={!!selectedOfferingId}
                placeholder={selectedOfferingId ? "Offering selected" : "Select product..."}
                testId="select-product"
                options={activeProducts.map(p => ({ value: p.id, label: p.name }))}
              />
              {/* Task #744 — optional variant picker (renders only when the product has variants). */}
              {selectedProduct && (
                <VariantPicker
                  product={selectedProduct}
                  value={selectedVariantName}
                  onChange={setSelectedVariantName}
                  testIdPrefix="instant-ad"
                />
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Tag className="h-3 w-3" /> Offering</label>
              <SearchableSelect
                value={selectedOfferingId}
                onValueChange={(v) => {
                  setSelectedOfferingId(v);
                  setSelectedProductId("");
                  setSelectedAngleId("");
                }}
                disabled={!!selectedProductId}
                placeholder={selectedProductId ? "Product selected" : "Select offering..."}
                testId="select-offering"
                options={offers.filter(o => o.status !== "archived").map(o => ({ value: o.id, label: o.name }))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><BookOpen className="h-3 w-3" /> Narrative Angle</label>
              <SearchableSelect
                value={selectedAngleId}
                onValueChange={setSelectedAngleId}
                placeholder="Select angle..."
                testId="select-angle"
                angles={filteredAngles}
              />
            </div>
          </div>

          {!isMeme && (
            <div className="rounded-xl border border-primary/30 bg-primary/[0.03] px-4 py-3.5 space-y-2.5">
              <div className="flex items-center gap-1.5">
                <Palette className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wider text-foreground">Example / style reference image</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Optional. Guides the look and feel of the generated ad — upload a sample creative whose style, composition, or mood you want the result to match.
              </p>
              <input type="file" ref={exampleFileRef} accept="image/*" className="hidden" onChange={handleExampleUpload} />
              {exampleImageUrl ? (
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg border overflow-hidden bg-muted/50 flex-shrink-0">
                    <img src={exampleImageUrl} alt="Example" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground">Example image uploaded</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-6 text-xs gap-1" onClick={() => exampleFileRef.current?.click()} data-testid="button-replace-example-image"><Pencil className="h-3 w-3" /> Replace</Button>
                      <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 text-destructive" onClick={() => updateAdUnitSetting({ adgenExampleImageUrl: null })} data-testid="button-remove-example-image"><Trash2 className="h-3 w-3" /> Remove</Button>
                    </div>
                  </div>
                </div>
              ) : (
                <Button variant="outline" size="sm" className="text-xs gap-1.5 h-8 border-primary/30" onClick={() => exampleFileRef.current?.click()} disabled={uploadingExample} data-testid="button-upload-example-image">
                  {uploadingExample ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  Upload example image
                </Button>
              )}
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-start gap-6 py-2 px-3 rounded-lg bg-muted/30 border border-border/30">
              <div className="flex items-center gap-3">
                <Switch id="is-meme" checked={isMeme} onCheckedChange={(checked) => updateAdUnitSetting({ isMeme: checked })} data-testid="switch-is-meme" />
                <Label htmlFor="is-meme" className="text-xs cursor-pointer">Meme format</Label>
              </div>
              {!isMeme && (
                <div className="flex items-center gap-3">
                  <Switch id="include-product-image" checked={includeProductImage} onCheckedChange={(checked) => updateAdUnitSetting({ adgenIncludeProductImage: checked })} data-testid="switch-include-product-image" />
                  <Label htmlFor="include-product-image" className="text-xs cursor-pointer">Include product/offering image</Label>
                </div>
              )}
            </div>

            {isMeme && (
              <div className="py-2 px-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-3.5 w-3.5 text-amber-600" />
                  <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">Meme Reference Image</span>
                </div>
                <p className="text-[11px] text-muted-foreground mb-2">Only this reference image will be sent to the image generator. No product photos or brand logos will be included.</p>
                <input type="file" ref={memeRefFileRef} accept="image/*" className="hidden" onChange={handleMemeRefUpload} />
                {memeReferenceImageUrl ? (
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 rounded-lg border overflow-hidden bg-muted/50 flex-shrink-0">
                      <img src={memeReferenceImageUrl} alt="Meme reference" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-muted-foreground">Reference uploaded</span>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-6 text-xs gap-1" onClick={() => memeRefFileRef.current?.click()} data-testid="button-replace-meme-ref"><Pencil className="h-3 w-3" /> Replace</Button>
                        <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 text-destructive" onClick={() => updateAdUnitSetting({ memeReferenceImageUrl: null })} data-testid="button-remove-meme-ref"><Trash2 className="h-3 w-3" /> Remove</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" className="text-xs gap-1.5 h-8 border-amber-500/30" onClick={() => memeRefFileRef.current?.click()} disabled={uploadingMemeRef} data-testid="button-upload-meme-ref">
                    {uploadingMemeRef ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                    Upload meme reference
                  </Button>
                )}
              </div>
            )}
          </div>

          {promptGenerated && (
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Layers className="h-3 w-3" /> Aspect Ratios
              </label>
              <div className="flex flex-col gap-2 py-2 px-3 rounded-lg bg-muted/30 border border-border/30">
                <div className="flex items-center gap-4 flex-wrap">
                  {ASPECT_RATIOS.map((ratio) => {
                    const checked = selectedRatios.includes(ratio);
                    const isBase = checked && (selectedRatios.includes(baseRatio) ? baseRatio === ratio : selectedRatios[0] === ratio);
                    return (
                      <div key={ratio} className="flex items-center gap-2">
                        <Checkbox
                          id={`ratio-${RATIO_TESTID[ratio]}`}
                          checked={checked}
                          onCheckedChange={(v) => {
                            setSelectedRatios((prev) => {
                              const next = v
                                ? Array.from(new Set([...prev, ratio]))
                                : prev.filter((r) => r !== ratio);
                              // If we're unchecking the current base, hand
                              // base off to whatever's still checked.
                              if (!v && ratio === baseRatio && next.length > 0) {
                                setBaseRatio(next[0]);
                              }
                              return next;
                            });
                          }}
                          data-testid={`checkbox-ratio-${RATIO_TESTID[ratio]}`}
                        />
                        <Label htmlFor={`ratio-${RATIO_TESTID[ratio]}`} className="text-xs cursor-pointer">
                          {RATIO_LABELS[ratio]}
                        </Label>
                        {isBase && (
                          <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                            Base
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {selectedRatios.length > 1 && (
                  <div className="flex items-center gap-3 flex-wrap pt-1 border-t border-border/40">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Base ratio
                    </span>
                    {selectedRatios.map((ratio) => (
                      <button
                        key={ratio}
                        type="button"
                        onClick={() => setBaseRatio(ratio)}
                        className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                          baseRatio === ratio
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border/60 text-muted-foreground hover:bg-muted"
                        }`}
                        data-testid={`button-base-ratio-${RATIO_TESTID[ratio]}`}
                      >
                        {ratio}
                      </button>
                    ))}
                    <span className="text-[10px] text-muted-foreground">
                      Rendered fresh; the others are reframed from it.
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border/60 bg-muted/30 px-4 py-3.5 space-y-2.5">
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                Creative Mode
              </span>
            </div>
            <div className="inline-flex flex-wrap items-center gap-1 rounded-full border border-border/60 bg-background p-1">
              {creativeStyles.map((style) => {
                const isSelected = creativeStyleMode === style.modeKey;
                const suffix = CREATIVE_MODE_LABEL_SUFFIX[style.modeKey] || "";
                return (
                  <button
                    key={style.modeKey}
                    type="button"
                    onClick={() => {
                      if (style.modeKey === creativeStyleMode) return;
                      // Switching modes invalidates the assembled prompt and any
                      // generated images (they were built against the previous
                      // mode's modifier text). Clear them so the user can't
                      // forget to regenerate. Warn first if any rendered image
                      // hasn't been saved to the library yet.
                      const hasUnsavedImage = Object.values(results).some(
                        (r) => (r?.generatedImage && !r?.savedNb) || (r?.openaiImage && !r?.savedOa)
                      );
                      if (hasUnsavedImage) {
                        const ok = window.confirm(
                          "Switching Creative Mode will clear the current resolved prompt and generated images. Any images you haven't saved to the library will be lost. Continue?"
                        );
                        if (!ok) return;
                      }
                      setPromptGenerated(false);
                      setResolvedPrompt("");
                      setResults({});
                      setCreativeStyleMode(style.modeKey);
                    }}
                    className={`text-xs font-medium px-3.5 py-1.5 rounded-full transition-colors ${
                      isSelected
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                    data-testid={`button-creative-style-${style.modeKey}`}
                  >
                    {style.displayName}{suffix}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed" data-testid="text-creative-style-helper">
              {CREATIVE_MODE_HELPER_TEXT[creativeStyleMode] ?? ""}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={handleGeneratePrompt} disabled={generatingPrompt} variant="outline" className="rounded-full gap-2" data-testid="button-generate-nb-prompt">
              {generatingPrompt ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating Prompt...</> : <><Brain className="h-4 w-4" /> Generate Image Prompt</>}
            </Button>
            {promptGenerated && (
              <Button
                onClick={handleGenerate}
                disabled={generating || !resolvedPrompt.trim() || selectedRatios.length === 0}
                className="rounded-full gap-2"
                data-testid="button-generate-image"
              >
                {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</> : <><Sparkles className="h-4 w-4" /> Generate Image</>}
              </Button>
            )}
            {!adUnit.imagePromptTemplate && !promptGenerated && (
              <p className="text-xs text-muted-foreground">Add a Custom Template Prompt first to enable generation.</p>
            )}
          </div>

          {resolvedPrompt && (
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Resolved Prompt</label>
              <Textarea value={resolvedPrompt} onChange={(e) => setResolvedPrompt(e.target.value)} rows={5} className="font-mono text-xs resize-y" data-testid="textarea-resolved-prompt" />
            </div>
          )}

          {(() => {
            // Only show tiles for ratios the user currently has checked, so
            // unchecking a box hides its old result immediately rather than
            // letting it linger next to a freshly-generated image.
            const ratiosWithData = ASPECT_RATIOS.filter((r) => {
              if (!selectedRatios.includes(r)) return false;
              const e = results[r];
              return e && (e.generatedImage || e.openaiImage || e.falError || e.openaiError);
            });
            if (ratiosWithData.length === 0 && !generating) return null;
            const ratiosToShow = generating ? selectedRatios : ratiosWithData;
            return (
              <div className="space-y-4 pt-2">
                <div className={compareModels && isAdmin
                  ? "space-y-6"
                  : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"}>
                  {ratiosToShow.map((ratio) => {
                    const entry = results[ratio] ?? emptyRatioResult;
                    const tid = RATIO_TESTID[ratio];
                    const isLoading = generating && !entry.generatedImage && !entry.openaiImage && !entry.falError && !entry.openaiError;
                    const effectiveBase: AspectRatio = selectedRatios.includes(baseRatio) ? baseRatio : (selectedRatios[0] ?? "4:5");
                    const isBase = ratio === effectiveBase;
                    const tileBadge = isBase ? (
                      <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary">Base</span>
                    ) : (
                      <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Reframed from {effectiveBase}</span>
                    );
                    if (compareModels && isAdmin) {
                      return (
                        <div key={ratio} className="space-y-2" data-testid={`group-ratio-${tid}`}>
                          <div className="flex items-center gap-2">
                            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                              {RATIO_LABELS[ratio]}
                            </div>
                            {tileBadge}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Nano Banana 2.1 / 2.0 (global setting)</span>
                                {entry.generatedImage && (
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 gap-1.5 text-xs"
                                      disabled={entry.savingNb || entry.savedNb}
                                      onClick={() => handleSaveToLibrary(ratio, "nb", entry.generatedImage!, "nano-banana-2")}
                                      data-testid={`button-save-nb-${tid}`}
                                    >
                                      {entry.savingNb ? <Loader2 className="h-3 w-3 animate-spin" /> : entry.savedNb ? <BookmarkCheck className="h-3 w-3" /> : <BookmarkPlus className="h-3 w-3" />}
                                      {entry.savedNb ? "Saved" : "Save"}
                                    </Button>
                                    <a href={entry.generatedImage} target="_blank" rel="noopener noreferrer" download>
                                      <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs" data-testid={`button-download-nb-${tid}`}>
                                        <Download className="h-3 w-3" /> Open
                                      </Button>
                                    </a>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 gap-1.5 text-xs"
                                      onClick={() => updateRatioResult(ratio, { inpaintOpen: true })}
                                      data-testid={`button-edit-nb-${tid}`}
                                    >
                                      <Paintbrush className="h-3 w-3" /> Edit
                                    </Button>
                                  </div>
                                )}
                              </div>
                              {isLoading ? (
                                <div className="rounded-xl border bg-muted/30 p-6 flex items-center justify-center text-xs text-muted-foreground">
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating...
                                </div>
                              ) : entry.generatedImage ? (
                                <div className="relative rounded-xl border overflow-hidden bg-muted/30">
                                  <AdImage src={entry.generatedImage} alt="Nano-Banana-2 output" className="w-full h-auto" data-testid={`img-generated-ad-${tid}`} />
                                </div>
                              ) : (
                                <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-xs text-destructive" data-testid={`text-fal-error-${tid}`}>
                                  Failed: {entry.falError || "no image returned"}
                                </div>
                              )}
                              {entry.generatedImage && (
                                <InpaintingModal
                                  open={entry.inpaintOpen}
                                  onClose={() => updateRatioResult(ratio, { inpaintOpen: false })}
                                  imageUrl={entry.generatedImage}
                                  brandId={brandId}
                                  onAccept={(newUrl) => updateRatioResult(ratio, { generatedImage: newUrl, inpaintOpen: false, savedNb: false, savingNb: false })}
                                />
                              )}
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{openaiModel} (OpenAI)</span>
                                {entry.openaiImage && (
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 gap-1.5 text-xs"
                                      disabled={entry.savingOa || entry.savedOa}
                                      onClick={() => handleSaveToLibrary(ratio, "oa", entry.openaiImage!, openaiModel)}
                                      data-testid={`button-save-openai-${tid}`}
                                    >
                                      {entry.savingOa ? <Loader2 className="h-3 w-3 animate-spin" /> : entry.savedOa ? <BookmarkCheck className="h-3 w-3" /> : <BookmarkPlus className="h-3 w-3" />}
                                      {entry.savedOa ? "Saved" : "Save"}
                                    </Button>
                                    <a href={entry.openaiImage} target="_blank" rel="noopener noreferrer" download>
                                      <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs" data-testid={`button-download-openai-${tid}`}>
                                        <Download className="h-3 w-3" /> Open
                                      </Button>
                                    </a>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 gap-1.5 text-xs"
                                      onClick={() => updateRatioResult(ratio, { inpaintOpenOa: true })}
                                      data-testid={`button-edit-openai-${tid}`}
                                    >
                                      <Paintbrush className="h-3 w-3" /> Edit
                                    </Button>
                                  </div>
                                )}
                              </div>
                              {isLoading ? (
                                <div className="rounded-xl border bg-muted/30 p-6 flex items-center justify-center text-xs text-muted-foreground">
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating...
                                </div>
                              ) : entry.openaiImage ? (
                                <div className="relative rounded-xl border overflow-hidden bg-muted/30">
                                  <AdImage src={entry.openaiImage} alt="OpenAI output" className="w-full h-auto" data-testid={`img-openai-ad-${tid}`} />
                                </div>
                              ) : (
                                <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-xs text-destructive" data-testid={`text-openai-error-${tid}`}>
                                  Failed: {entry.openaiError || "no image returned"}
                                </div>
                              )}
                              {entry.openaiImage && (
                                <InpaintingModal
                                  open={entry.inpaintOpenOa}
                                  onClose={() => updateRatioResult(ratio, { inpaintOpenOa: false })}
                                  imageUrl={entry.openaiImage}
                                  brandId={brandId}
                                  onAccept={(newUrl) => updateRatioResult(ratio, { openaiImage: newUrl, inpaintOpenOa: false, savedOa: false, savingOa: false })}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    // Non-compare: per-ratio tile with image + Save / Open / Edit
                    return (
                      <div key={ratio} className="space-y-2" data-testid={`tile-ratio-${tid}`}>
                        <div className="flex items-center gap-2">
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {RATIO_LABELS[ratio]}
                          </div>
                          {tileBadge}
                        </div>
                        {isLoading ? (
                          <div className="rounded-xl border bg-muted/30 p-8 flex items-center justify-center text-xs text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating...
                          </div>
                        ) : entry.generatedImage ? (
                          <>
                            <div className="relative rounded-xl border overflow-hidden bg-muted/30">
                              <AdImage src={entry.generatedImage} alt={`Generated ad ${ratio}`} className="w-full h-auto" data-testid={`img-generated-ad-${tid}`} />
                            </div>
                            <div className="flex gap-1.5 flex-wrap">
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-full gap-1.5 h-7 text-xs"
                                disabled={entry.savingNb || entry.savedNb}
                                onClick={() => handleSaveToLibrary(ratio, "nb", entry.generatedImage!, "nano-banana-2")}
                                data-testid={`button-save-generated-${tid}`}
                              >
                                {entry.savingNb ? <Loader2 className="h-3 w-3 animate-spin" /> : entry.savedNb ? <BookmarkCheck className="h-3 w-3" /> : <BookmarkPlus className="h-3 w-3" />}
                                {entry.savedNb ? "Saved" : "Save"}
                              </Button>
                              <a href={entry.generatedImage} target="_blank" rel="noopener noreferrer" download>
                                <Button variant="outline" size="sm" className="rounded-full gap-1.5 h-7 text-xs" data-testid={`button-download-generated-${tid}`}>
                                  <Download className="h-3 w-3" /> Open
                                </Button>
                              </a>
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-full gap-1.5 h-7 text-xs"
                                onClick={() => updateRatioResult(ratio, { inpaintOpen: true })}
                                data-testid={`button-edit-generated-${tid}`}
                              >
                                <Paintbrush className="h-3 w-3" /> Edit
                              </Button>
                              <InpaintingModal
                                open={entry.inpaintOpen}
                                onClose={() => updateRatioResult(ratio, { inpaintOpen: false })}
                                imageUrl={entry.generatedImage}
                                brandId={brandId}
                                onAccept={(newUrl) => updateRatioResult(ratio, { generatedImage: newUrl, inpaintOpen: false, savedNb: false, savingNb: false })}
                              />
                            </div>
                          </>
                        ) : (
                          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-xs text-destructive" data-testid={`text-fal-error-${tid}`}>
                            Failed: {entry.falError || "no image returned"}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {isAdmin && (
            <div className="pt-3 mt-3 border-t border-dashed border-border/60 space-y-2">
              <div className="flex items-start gap-3 py-2 px-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <Switch id="compare-models" checked={compareModels} onCheckedChange={setCompareModels} data-testid="switch-compare-models" />
                <div className="flex-1">
                  <Label htmlFor="compare-models" className="text-xs cursor-pointer flex items-center gap-1.5">
                    <FlaskConical className="h-3.5 w-3.5 text-amber-600" />
                    Compare Models (Nano Banana 2.1 / 2.0 global setting vs GPT-Image-2)
                  </Label>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Admin only. Sends the same NB Prompt to Nano Banana 2.1 / 2.0 (global setting) and OpenAI GPT-Image-2 in parallel and renders both results side by side for comparison.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 py-2 px-3 rounded-lg bg-violet-500/5 border border-violet-500/20">
                <Switch id="new-prompt-maker" checked={useNewPromptMaker} onCheckedChange={setUseNewPromptMaker} data-testid="switch-new-prompt-maker" />
                <div className="flex-1">
                  <Label htmlFor="new-prompt-maker" className="text-xs cursor-pointer flex items-center gap-1.5">
                    <FlaskConical className="h-3.5 w-3.5 text-violet-600" />
                    New Prompt Maker (experimental)
                  </Label>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Admin only. Routes the Generate Image Prompt call to the experimental Sim workflow instead of the production one. Affects this Instant Ad Generation card only.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 py-2 px-3 rounded-lg bg-sky-500/5 border border-sky-500/20">
                <Switch id="prompt-maker-v2" checked={usePromptMakerV2} onCheckedChange={setUsePromptMakerV2} data-testid="switch-prompt-maker-v2" />
                <div className="flex-1">
                  <Label htmlFor="prompt-maker-v2" className="text-xs cursor-pointer flex items-center gap-1.5">
                    <FlaskConical className="h-3.5 w-3.5 text-sky-600" />
                    Prompt Maker v2
                  </Label>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Admin only. Routes the Generate Image Prompt call to the v2 Sim workflow (the newer prompt maker Ads Express uses). Defaults to the no-logo variant. Takes precedence if both prompt-maker toggles are on. Affects this Instant Ad Generation card only.
                  </p>
                  {usePromptMakerV2 && (
                    <div className="flex items-start gap-3 mt-2 pt-2 border-t border-sky-500/20">
                      <Switch id="prompt-maker-v2-logo" checked={v2IncludeLogo} onCheckedChange={setV2IncludeLogo} data-testid="switch-prompt-maker-v2-logo" />
                      <div className="flex-1">
                        <Label htmlFor="prompt-maker-v2-logo" className="text-xs cursor-pointer">
                          Include logo
                        </Label>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Off (default): the no-logo v2 workflow — prompts never reference a brand logo and the logo image is left out of generation. On: the with-logo v2 workflow, keeping the brand logo reference.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
